import { LearningApi } from "@genesys/cloud-frontend/apis/LearningApi";
import { QualityApi } from "@genesys/cloud-frontend/apis/QualityApi";
import {
    AnswerOptionBuiltInTypeEnum,
    User,
} from "@genesys/cloud-frontend/dist/mjs/models";
import {
    AiScoringFailureTypeEnum,
    AiAnswerFailureTypeEnum,
    AssessmentForm,
    AssessmentFormQuestion,
    AssessmentFormQuestionTypeEnum,
    AssessmentQuestionGroupScore,
    AssessmentQuestionScore,
    AssistanceConditionOperatorEnum,
    Calibration,
    EvaluationForm,
    EvaluationFormResponse,
    EvaluationQuestion,
    EvaluationQuestionScore,
    EvaluationQuestionTypeEnum,
    EvaluationResponse,
    EvaluationResponseStatusEnum,
    EvaluationScoringSet,
    EvaluationStatusEnum,
    LearningAssessment,
    LearningAssessmentStatusEnum,
    ScorableSurvey,
    ScorableSurveyStatusEnum,
    Survey,
    SurveyForm,
    SurveyFromJSON,
    SurveyQuestionGroupScore,
    SurveyQuestionScore,
    SurveyQuestionTypeEnum,
    SurveyStatusEnum,
    TranscriptTopic,
} from "@genesys/cloud-frontend/models";
import {
    Dispatch,
    PayloadAction,
    createAsyncThunk,
    createSlice,
    isAnyOf,
} from "@reduxjs/toolkit";
import { RejectedActionFromAsyncThunk } from "@reduxjs/toolkit/src/matchers";

import { RootState } from "app/store";
import { DefaultLimits } from "constants/DefaultLimits";
import AnswerSource from "enums/AnswerSource";
import AuthorizedActions from "enums/AuthorizedActions";
import CommentType from "enums/CommentType";
import { ErrorCodes } from "enums/ErrorCodes";
import FeatureToggle from "enums/FeatureToggle";
import FormType from "enums/FormType";
import ModuleStatus from "enums/ModuleStatus";
import { SideDrawerMode } from "enums/SideDrawerMode";
import QualityFormPermission from "enums/qualityFormPermission";
import { selectPermission, selectUser } from "features/auth/currentUserSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    getDenormalizedQualityForm,
    getEvaluationSettings,
    loadForm,
    selectAssistedConditionForAnswer,
    selectQuestionGroup,
    selectQuestionGroupIds,
    selectQuestionIds,
    selectTotalVisibleQuestionsWithAiScoring,
    selectVisibleQuestionGroupIds,
    selectVisibleQuestionIds,
} from "features/qualityForm/qualityFormSlice";
import {
    isUndefinedOrNull,
    selectAnswerOptionMutable,
    selectForm,
    selectFormType,
    selectQuestion,
    selectScorableQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    COMMENTS_CHARACTER_LIMIT_EFFECTIVE_DATE,
    selectAreSurveyAnswersUnderLimit,
    selectIsVisibleScorableQuestionGroupValid,
    selectIsScorableQuestionValid,
    selectIsScorableValid,
} from "features/scorable/ScorableFormValidations";
import moment from "moment-timezone";
import { denormalize, normalize, schema } from "normalizr";
import apiCore from "services/apiCore";
import authService from "services/authService";
import embeddingService from "services/embeddingService";
import qualityManagementService from "services/qualityManagementService";
import HttpError from "util/HttpError";
import {
    QuestionScore,
    NormalizedQuestionGroupScores,
    NormalizedScorable,
    QuestionGroupScore,
    PreviewScoreResponse,
    LoadFormArg,
    DefaultAnswer,
} from "./scorableSlices/scorableTypes";
import { ScorableState } from "./scorableSlices/scorableStates";
import { ScoreTagLevel } from "features/common/ScoreTag";
import { API_CONFIGURATION } from "util/apiUtils";

const questionScoresSchema = new schema.Entity<QuestionScore>(
    "questionScores",
    undefined,
    { idAttribute: "questionId" }
);

const questionGroupScoresSchema = new schema.Entity<QuestionGroupScore>(
    "questionGroupScores",
    {
        questionScores: [questionScoresSchema],
    },
    { idAttribute: "questionGroupId" }
);

const scorableSchema = new schema.Entity<NormalizedScorable>("scorables", {
    answers: {
        questionGroupScores: [questionGroupScoresSchema],
    },
    evaluator: {},
});

export function loadFormFulfilledActionCreator(
    form: EvaluationFormResponse | SurveyForm | AssessmentForm,
    formType: FormType
) {
    const metaArgs =
        formType === FormType.ASSESSMENT
            ? {
                  formType,
                  formId: form.id!,
                  moduleStatus: ModuleStatus.PUBLISHED,
              }
            : { formType, formId: form.id! };
    return loadForm.fulfilled(form, "", metaArgs);
}

export function isNotNull<T>(topicId: T | undefined): topicId is T {
    return topicId !== undefined;
}

function getHighestAnswer({
    question,
}: {
    question: EvaluationQuestion | AssessmentFormQuestion;
}) {
    return question.answerOptions?.reduce((prev, current) => {
        const prevValue = prev?.value ?? 0;
        const currentValue = current?.value ?? 0;
        return prevValue >= currentValue ? prev : current;
    });
}

function getLowestAnswer({
    question,
}: {
    question: EvaluationQuestion | AssessmentFormQuestion;
}) {
    return question.answerOptions?.reduce((prev, current) => {
        const prevValue = prev?.value ?? 0;
        const currentValue = current?.value ?? 0;
        return prevValue <= currentValue ? prev : current;
    });
}

function getDefaultHighestScoreAnswers(
    form: EvaluationFormResponse
): DefaultAnswer {
    const defaultAnswers: DefaultAnswer = {};
    form?.questionGroups
        ?.filter((questionGroup) => questionGroup.defaultAnswersToHighest)
        .forEach((questionGroup) =>
            questionGroup.questions
                ?.filter(
                    (question) =>
                        question.type ===
                            EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
                        question.type ===
                            EvaluationQuestionTypeEnum.MultipleSelectQuestion
                )
                .forEach((question) => {
                    if (
                        question.type ===
                        EvaluationQuestionTypeEnum.MultipleChoiceQuestion
                    ) {
                        const highestAnswer = getHighestAnswer({ question });
                        if (highestAnswer) {
                            defaultAnswers[question.id!] = {
                                answerId: highestAnswer.id,
                                answerSource: AnswerSource.DEFAULT,
                            };
                        }
                    } else {
                        // iterate question.multipleSelectQuestionOptions
                        // set the answer ID as the selected ID for every option
                        const defaultMultipleSelectQuestionAnswers =
                            question.multipleSelectOptionQuestions?.map(
                                (option) => {
                                    // find the answerOption item with builtInType Selected in the option.answerOptions
                                    const selectedAnswerOption =
                                        option.answerOptions?.find(
                                            (answerOption) =>
                                                answerOption.builtInType ===
                                                AnswerOptionBuiltInTypeEnum.Selected
                                        );
                                    return {
                                        questionId: option.id!,
                                        answerId: selectedAnswerOption?.id,
                                        answerSource: AnswerSource.DEFAULT,
                                    };
                                }
                            ) ?? [];
                        defaultAnswers[question.id!] = {
                            multipleSelectQuestionOptionScores:
                                defaultMultipleSelectQuestionAnswers as EvaluationQuestionScore[],
                            answerSource: AnswerSource.DEFAULT,
                        };
                    }
                })
        );
    return defaultAnswers;
}

function getDefaultNAAnswers(form: EvaluationFormResponse): DefaultAnswer {
    const defaultAnswers: DefaultAnswer = {};
    form?.questionGroups
        ?.filter((questionGroup) => questionGroup.defaultAnswersToNA)
        .forEach((questionGroup) =>
            questionGroup.questions
                ?.filter(
                    (question) =>
                        question.type ===
                            EvaluationQuestionTypeEnum.MultipleChoiceQuestion &&
                        question.naEnabled
                )
                .forEach((question) => {
                    defaultAnswers[question.id!] = {
                        answerId: "",
                        markedNA: true,
                        answerSource: AnswerSource.DEFAULT,
                    };
                })
        );
    return defaultAnswers;
}

function getDefaultAnswers(form: EvaluationFormResponse): DefaultAnswer {
    const defaultAnswers: DefaultAnswer = {};
    form?.questionGroups
        ?.filter(
            (questionGroup) =>
                questionGroup.defaultAnswersTo &&
                (questionGroup.defaultAnswersTo.notApplicable ||
                    questionGroup.defaultAnswersTo.highestScore ||
                    questionGroup.defaultAnswersTo.lowestScore ||
                    questionGroup.defaultAnswersTo.userDefined)
        )
        .forEach((questionGroup) =>
            questionGroup.questions
                ?.filter(
                    (question) =>
                        question.type ===
                            EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
                        question.type ===
                            EvaluationQuestionTypeEnum.MultipleSelectQuestion
                )
                .forEach((question) => {
                    if (
                        question.type ===
                        EvaluationQuestionTypeEnum.MultipleChoiceQuestion
                    ) {
                        if (question.defaultAnswer) {
                            if (
                                question.defaultAnswer.notApplicable &&
                                question.naEnabled &&
                                questionGroup.defaultAnswersTo?.notApplicable
                            ) {
                                defaultAnswers[question.id!] = {
                                    answerId: "",
                                    markedNA: true,
                                    answerSource: AnswerSource.DEFAULT,
                                };
                            } else if (question.defaultAnswer.id) {
                                defaultAnswers[question.id!] = {
                                    answerId: question.defaultAnswer.id,
                                    answerSource: AnswerSource.DEFAULT,
                                };
                            }
                        }
                    } else if (
                        question.type ===
                        EvaluationQuestionTypeEnum.MultipleSelectQuestion
                    ) {
                        // iterate question.multipleSelectQuestionOptions
                        const defaultMultipleSelectQuestionAnswers =
                            question.multipleSelectOptionQuestions
                                ?.filter((option) => option.defaultAnswer?.id)
                                .map((option) => {
                                    return {
                                        questionId: option.id!,
                                        answerId: option.defaultAnswer?.id,
                                        answerSource: AnswerSource.DEFAULT,
                                    };
                                })
                                .filter(
                                    (defaultAnswer) => defaultAnswer !== null
                                ) ?? [];
                        if (defaultMultipleSelectQuestionAnswers.length > 0) {
                            defaultAnswers[question.id!] = {
                                multipleSelectQuestionOptionScores:
                                    defaultMultipleSelectQuestionAnswers as EvaluationQuestionScore[],
                                answerSource: AnswerSource.DEFAULT,
                            };
                        }
                    }
                })
        );
    return defaultAnswers;
}

function getDefaultFreeTextAnswer(
    form: SurveyForm | AssessmentForm
): DefaultAnswer {
    const defaultAnswers: DefaultAnswer = {};
    form?.questionGroups?.forEach((questionGroup) => {
        if (questionGroup.questions) {
            questionGroup.questions.forEach((question) => {
                if (
                    question &&
                    (question.type ===
                        SurveyQuestionTypeEnum.FreeTextQuestion ||
                        question.type ===
                            AssessmentFormQuestionTypeEnum.FreeTextQuestion) &&
                    question.naEnabled
                ) {
                    defaultAnswers[question.id!] = {
                        markedNA: true,
                        answerSource: AnswerSource.DEFAULT,
                    };
                }
            });
        }
    });
    return defaultAnswers;
}

function getDefaultAutomatedScoringAnswers(
    isLensifyFTEnabled: boolean,
    scoringSet?: EvaluationScoringSet
): DefaultAnswer {
    const defaultAnswers: DefaultAnswer = {};

    scoringSet?.questionGroupScores?.forEach((questionGroupScore) => {
        questionGroupScore.questionScores?.forEach((questionScore) => {
            // handle MSQ scores
            if (questionScore.multipleSelectQuestionOptionScores) {
                const multipleSelectQuestionOptionsAnswers: EvaluationQuestionScore[] =
                    [];
                let hasAssistedOptions = false;
                // only need to take care of assisted answer IDs
                questionScore.multipleSelectQuestionOptionScores.forEach(
                    (optionScore) => {
                        const hasAnswerId =
                            optionScore.answerId !== "" &&
                            !isUndefinedOrNull(optionScore.answerId);
                        const hasAssistedAnswerId =
                            optionScore.assistedAnswerId !== "" &&
                            !isUndefinedOrNull(optionScore.assistedAnswerId);
                        if (!hasAnswerId && hasAssistedAnswerId) {
                            hasAssistedOptions = true;
                            multipleSelectQuestionOptionsAnswers.push({
                                ...optionScore,
                                questionId: optionScore.questionId!,
                                answerId: optionScore.assistedAnswerId!,
                            });
                        }
                    }
                );

                if (hasAssistedOptions) {
                    defaultAnswers[questionScore.questionId!] = {
                        multipleSelectQuestionOptionScores:
                            multipleSelectQuestionOptionsAnswers,
                        markedNA: false,
                        answerSource: AnswerSource.EVALUATION_ASSISTANCE,
                    };
                }
            } else {
                // hanlde non-MSQ scores
                const hasAnswerId =
                    questionScore.answerId !== "" &&
                    questionScore.answerId !== undefined;
                const hasAssistedAnswerId =
                    questionScore.assistedAnswerId !== "" &&
                    questionScore.assistedAnswerId !== undefined;
                const hasAiAnswerId =
                    questionScore.aiAnswer?.answerId !== "" &&
                    questionScore.aiAnswer?.answerId !== undefined;
                const hasAiAnswerFailure =
                    questionScore.aiAnswer?.failureType !== undefined &&
                    questionScore.aiAnswer?.failureType !== null;
                const hasAiAnswerMarkedNotApplicable =
                    questionScore.aiAnswer?.markedNotApplicable === true;

                if (!hasAnswerId && hasAssistedAnswerId) {
                    defaultAnswers[questionScore.questionId!] = {
                        answerId: questionScore.assistedAnswerId,
                        markedNA: false,
                        answerSource: AnswerSource.EVALUATION_ASSISTANCE,
                    };
                }

                if (!hasAnswerId && !hasAiAnswerFailure && hasAiAnswerId) {
                    defaultAnswers[questionScore.questionId!] = {
                        answerId: questionScore.aiAnswer?.answerId,
                        markedNA: false,
                        answerSource: AnswerSource.AI_SCORING,
                    };
                }

                // Handle AI-suggested MarkedNA answer
                if (
                    isLensifyFTEnabled &&
                    !hasAnswerId &&
                    !hasAiAnswerFailure &&
                    !hasAiAnswerId &&
                    hasAiAnswerMarkedNotApplicable
                ) {
                    defaultAnswers[questionScore.questionId!] = {
                        markedNA: true,
                        answerSource: AnswerSource.AI_SCORING,
                    };
                }
            }
        });
    });
    return defaultAnswers;
}

function sanitizeAnswers(answers: any, state: RootState): void {
    // fill MCQ answers
    answers?.questionGroupScores?.forEach((score: QuestionGroupScore) => {
        if (score) {
            // unsetting markedNA flag if the question group was not configured to support NA option.
            if (
                score.markedNA &&
                !selectQuestionGroup(state, score?.questionGroupId ?? "")
                    ?.naEnabled
            ) {
                score.markedNA = false;
            }
            if (score.questionScores) {
                score.questionScores.forEach((questionScore) => {
                    if (questionScore) {
                        // unsetting markedNA flag if the question was not configured to support NA option.
                        if (
                            questionScore.markedNA &&
                            !selectQuestion(
                                state,
                                questionScore.questionId ?? ""
                            )?.naEnabled
                        ) {
                            questionScore.markedNA = false;
                        }
                        if (!questionScore.answerId) {
                            questionScore.answerId = "";
                        }
                    }
                });
            }
        }
    });
}

function getEmptyScoreSet(
    form: EvaluationFormResponse | SurveyForm | AssessmentForm | undefined
) {
    const questionGroupScores = (form?.questionGroups ?? []).map(
        (questionGroup) => {
            const questionScores = questionGroup.questions?.map((question) => {
                return {
                    questionId: question.id,
                };
            });
            return { questionGroupId: questionGroup.id!, questionScores };
        }
    );

    return { questionGroupScores };
}

export const isInitiallyHiddenOnPageLoadAndCanResetMarkedNA = ({
    isMarkedNA,
    isSystemMarkedNA,
    canResetMarkedNABasedOnSystemMarkedNA,
}: {
    isMarkedNA: boolean | undefined;
    isSystemMarkedNA: boolean | undefined;
    canResetMarkedNABasedOnSystemMarkedNA: boolean;
}) => {
    return canResetMarkedNABasedOnSystemMarkedNA
        ? isMarkedNA && isSystemMarkedNA
        : false;
};

function setDefaultAnswers(
    defaultAnswers: DefaultAnswer,
    scoringSet: EvaluationScoringSet,
    formType: FormType,
    canResetMarkedNABasedOnSystemMarkedNA: boolean
) {
    scoringSet?.questionGroupScores?.forEach((questionGroup) => {
        if (
            formType === FormType.ASSESSMENT &&
            isInitiallyHiddenOnPageLoadAndCanResetMarkedNA({
                isMarkedNA: questionGroup.markedNA,
                isSystemMarkedNA: questionGroup.systemMarkedNA,
                canResetMarkedNABasedOnSystemMarkedNA,
            })
        ) {
            questionGroup.markedNA = false;
            questionGroup.systemMarkedNA = false;
        }

        questionGroup.questionScores?.forEach((questionScore) => {
            const defaultScore = defaultAnswers[questionScore.questionId!];

            // To avoid any regression, only do this for evaluations and assessments
            if (
                (formType === FormType.EVALUATION ||
                    formType === FormType.ASSESSMENT) &&
                isInitiallyHiddenOnPageLoadAndCanResetMarkedNA({
                    isMarkedNA: questionScore.markedNA,
                    isSystemMarkedNA: questionScore.systemMarkedNA,
                    canResetMarkedNABasedOnSystemMarkedNA,
                })
            ) {
                questionScore.markedNA = false;
                questionScore.systemMarkedNA = false;
            }

            // when canResetMarkedNABasedOnSystemMarkedNA == false,
            // we want to preserve the old behaviour where this block doesn't care the markedNA value at all.
            const isUserMarkedNA = canResetMarkedNABasedOnSystemMarkedNA
                ? questionScore.markedNA && !questionScore.systemMarkedNA
                : false;

            const answerExists =
                isUserMarkedNA ||
                (questionScore.answerId !== "" &&
                    questionScore.answerId !== undefined);

            if (answerExists) {
                questionScore["answerSource"] = AnswerSource.SYSTEM;
            } else if (defaultScore) {
                questionScore.answerId = defaultScore.answerId;
                questionScore.markedNA = defaultScore.markedNA;
                questionScore["answerSource"] = defaultScore.answerSource;

                // TODO: check the case when we have both highest default answer + assistance answer
                if (
                    (defaultScore.multipleSelectQuestionOptionScores ?? [])
                        .length > 0
                ) {
                    // TODO: go through every option score
                    questionScore.multipleSelectQuestionOptionScores =
                        defaultScore.multipleSelectQuestionOptionScores;
                }
            }
        });
    });
}

export function getDefaultSelectedAnswers(
    form: EvaluationFormResponse | AssessmentForm | SurveyForm | undefined,
    formType: FormType | undefined,
    isDefaultAnsweringEnabled = false
) {
    const questionGroupScores = (form?.questionGroups ?? []).map(
        (questionGroup) => {
            const questionScores = questionGroup.questions?.map((question) => {
                if (
                    formType !== FormType.EVALUATION &&
                    (question.type ===
                        SurveyQuestionTypeEnum.FreeTextQuestion ||
                        question.type ===
                            AssessmentFormQuestionTypeEnum.FreeTextQuestion) &&
                    question.naEnabled
                ) {
                    return {
                        markedNA: true,
                        questionId: question.id!,
                        answerSource: AnswerSource.DEFAULT,
                    };
                }
                const isDefaultAnswersToHighestEnabled =
                    isDefaultAnsweringEnabled
                        ? "defaultAnswersTo" in questionGroup &&
                          questionGroup.defaultAnswersTo?.highestScore
                        : "defaultAnswersToHighest" in questionGroup &&
                          questionGroup.defaultAnswersToHighest;
                if (
                    formType !== FormType.SURVEY &&
                    (question.type ===
                        EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
                        question.type ===
                            AssessmentFormQuestionTypeEnum.MultipleChoiceQuestion)
                ) {
                    const defaultAnswersToExists =
                        isDefaultAnsweringEnabled &&
                        "defaultAnswersTo" in questionGroup;

                    const useNewDefaultAnswerFields =
                        isDefaultAnsweringEnabled &&
                        formType === FormType.EVALUATION;
                    const isDefaultAnswersToNaEnabled =
                        useNewDefaultAnswerFields
                            ? "defaultAnswersTo" in questionGroup &&
                              questionGroup.defaultAnswersTo?.notApplicable
                            : "defaultAnswersToNA" in questionGroup &&
                              questionGroup.defaultAnswersToNA;
                    if (isDefaultAnswersToNaEnabled && question.naEnabled) {
                        return {
                            questionId: question.id!,
                            answerId: "",
                            markedNA: true,
                            answerSource: AnswerSource.DEFAULT,
                        };
                    } else if (
                        formType !== FormType.ASSESSMENT &&
                        isDefaultAnswersToHighestEnabled
                    ) {
                        const highestAnswer = getHighestAnswer({ question });
                        return {
                            questionId: question.id!,
                            answerId: highestAnswer?.id,
                            value: highestAnswer?.value,
                            answerSource: AnswerSource.DEFAULT,
                        };
                    } else if (
                        formType !== FormType.ASSESSMENT &&
                        defaultAnswersToExists &&
                        questionGroup.defaultAnswersTo?.lowestScore
                    ) {
                        const lowestAnswer = getLowestAnswer({ question });
                        return {
                            questionId: question.id!,
                            answerId: lowestAnswer?.id,
                            value: lowestAnswer?.value,
                            answerSource: AnswerSource.DEFAULT,
                        };
                    } else if (
                        formType !== FormType.ASSESSMENT &&
                        defaultAnswersToExists &&
                        questionGroup.defaultAnswersTo?.userDefined &&
                        question.defaultAnswer?.id
                    ) {
                        return {
                            questionId: question.id!,
                            answerId: question.defaultAnswer.id,
                            answerSource: AnswerSource.DEFAULT,
                        };
                    } else {
                        return {
                            questionId: question.id!,
                        };
                    }
                } else if (
                    formType === FormType.EVALUATION &&
                    question.type ===
                        EvaluationQuestionTypeEnum.MultipleSelectQuestion
                ) {
                    const defaultAnswerToHighest =
                        isDefaultAnswersToHighestEnabled;
                    const defaultAnswerToUserDefined =
                        isDefaultAnsweringEnabled &&
                        "defaultAnswersTo" in questionGroup &&
                        questionGroup.defaultAnswersTo?.userDefined;

                    // in preview mode, we set the answerId as Selected by default
                    // only if the question group is configured with the defaultAnswerToHighest setting
                    // otherwise, we will set Unselected ID as answerId.
                    const defaultMultipleSelectQuestionAnswers =
                        question.multipleSelectOptionQuestions?.map(
                            (option) => {
                                let selectedAnswerOption;
                                if (
                                    defaultAnswerToUserDefined &&
                                    option.defaultAnswer?.id
                                ) {
                                    // For user-defined answers, use the default answer from the option
                                    selectedAnswerOption = {
                                        id: option.defaultAnswer.id,
                                    };
                                } else {
                                    selectedAnswerOption =
                                        option.answerOptions?.find(
                                            (answerOption) =>
                                                answerOption.builtInType ===
                                                (defaultAnswerToHighest
                                                    ? AnswerOptionBuiltInTypeEnum.Selected
                                                    : AnswerOptionBuiltInTypeEnum.Unselected)
                                        );
                                }
                                return {
                                    questionId: option.id!,
                                    answerId: selectedAnswerOption?.id ?? "",
                                };
                            }
                        ) ?? [];
                    return {
                        questionId: question.id!,
                        multipleSelectQuestionOptionScores:
                            defaultMultipleSelectQuestionAnswers,
                        answerSource: AnswerSource.DEFAULT,
                    };
                } else {
                    return {
                        questionId: question.id!,
                    };
                }
            });
            return { questionGroupId: questionGroup.id!, questionScores };
        }
    );

    return { questionGroupScores };
}

function isExpectedEvaluationStatusInResponse(
    evaluationResponse: EvaluationResponse,
    expectedStatus: EvaluationResponseStatusEnum
) {
    return evaluationResponse && evaluationResponse.status === expectedStatus;
}

function setAnswersForInProgressEvaluation(
    isLensifyFTEnabled: boolean,
    evaluationResponse: EvaluationResponse,
    canResetMarkedNABasedOnSystemMarkedNA: boolean
) {
    if (
        evaluationResponse &&
        evaluationResponse.answers &&
        isExpectedEvaluationStatusInResponse(
            evaluationResponse,
            EvaluationResponseStatusEnum.INPROGRESS
        )
    ) {
        const automatedScoringAnswers = getDefaultAutomatedScoringAnswers(
            isLensifyFTEnabled,
            evaluationResponse.answers
        );

        setDefaultAnswers(
            automatedScoringAnswers,
            evaluationResponse.answers,
            FormType.EVALUATION,
            canResetMarkedNABasedOnSystemMarkedNA
        );
    }
}

function resetMarkedNAForHiddenQuestionsInEvaluation(
    evaluationResponse: EvaluationResponse,
    canResetMarkedNABasedOnSystemMarkedNA: boolean
) {
    if (evaluationResponse && evaluationResponse.answers) {
        setDefaultAnswers(
            {},
            evaluationResponse.answers,
            FormType.EVALUATION,
            canResetMarkedNABasedOnSystemMarkedNA
        );
    }
}

const setAnswersForAssessmentInProgress = (
    assessment: LearningAssessment | undefined,
    isSystemMarkedNaFeatureEnabled: boolean
) => {
    if (
        assessment &&
        assessment.status === LearningAssessmentStatusEnum.InProgress &&
        assessment.answers
    ) {
        setDefaultAnswers(
            {},
            assessment.answers,
            FormType.ASSESSMENT,
            isSystemMarkedNaFeatureEnabled
        );
    }
};

// No need for checking the AI Scoring FT here since it's used only in the new thunk for AI Scoring feature
const updateAiScoringAnswers = (
    scoringSet: EvaluationScoringSet,
    existingQuestionScores: Record<string, QuestionScore | undefined>,
    isSystemMarkedNaFeatureEnabled: boolean,
    isLensifyFTEnabled: boolean
) => {
    scoringSet?.questionGroupScores?.forEach((questionGroupScore) => {
        questionGroupScore.questionScores?.forEach(
            (questionScore, index, thisQuestionScores) => {
                if (!questionScore.questionId) {
                    return;
                }
                const hasAnswerIdInResponse =
                    questionScore.answerId !== "" &&
                    questionScore.answerId !== undefined;
                const isSystemMarkedNA = isSystemMarkedNaFeatureEnabled
                    ? questionScore?.systemMarkedNA
                    : false;
                const hasUserMarkedNAInResponse =
                    questionScore?.markedNA && !isSystemMarkedNA;
                const savedUserAnswerExists =
                    hasAnswerIdInResponse || hasUserMarkedNAInResponse;

                const draftUserAnswerExists =
                    existingQuestionScores[questionScore.questionId]?.[
                        "answerSource"
                    ] === AnswerSource.USER || false;

                const hasAiAnswerId =
                    questionScore.aiAnswer?.answerId !== "" &&
                    questionScore.aiAnswer?.answerId !== undefined;
                const hasAiAnswerFailure =
                    questionScore.aiAnswer?.failureType !== undefined &&
                    questionScore.aiAnswer?.failureType !== null;
                const hasAiAnswerMarkedNA =
                    questionScore.aiAnswer?.markedNotApplicable === true;
                const hasValidAiAnswer =
                    (hasAiAnswerId || hasAiAnswerMarkedNA) &&
                    !hasAiAnswerFailure;

                if (
                    !savedUserAnswerExists &&
                    !draftUserAnswerExists &&
                    hasValidAiAnswer
                ) {
                    if (hasAiAnswerId) {
                        thisQuestionScores[index] = {
                            ...questionScore,
                            answerId: questionScore.aiAnswer?.answerId,
                            markedNA: false,
                        };
                    } else {
                        // has AiAnswerMarkedNA
                        if (isLensifyFTEnabled) {
                            thisQuestionScores[index] = {
                                ...questionScore,
                                answerId: undefined,
                                markedNA: true,
                            };
                        } else {
                            thisQuestionScores[index] = {
                                ...questionScore,
                                answerId: undefined,
                                markedNA: false,
                            };
                        }
                    }

                    thisQuestionScores[index]["answerSource"] =
                        AnswerSource.AI_SCORING;
                } else {
                    thisQuestionScores[index] = {
                        ...existingQuestionScores[questionScore.questionId],
                        aiAnswer: questionScore.aiAnswer,
                    };

                    // Clear the current answer if the previous answer source is Ai Scoring and the latest Ai Scoring result failed
                    const isAiScoringSource =
                        existingQuestionScores[questionScore.questionId]?.[
                            "answerSource"
                        ] === AnswerSource.AI_SCORING || false;
                    if (
                        !savedUserAnswerExists &&
                        !hasValidAiAnswer &&
                        isAiScoringSource
                    ) {
                        thisQuestionScores[index].answerId = undefined;
                        thisQuestionScores[index].markedNA = false;
                        thisQuestionScores[index]["answerSource"] = undefined;
                    }
                }

                // We want to keep the comments that are already present in the UI before the api call,
                // and we will ignore the ones in the api response when updating the ai scoring answers
                thisQuestionScores[index].comments =
                    existingQuestionScores[questionScore.questionId]?.comments;
            }
        );
    });
};

export const canEditScore = (authorizedActions: string[] | undefined) => {
    if (authorizedActions && authorizedActions.length) {
        return (
            authorizedActions.includes(AuthorizedActions.EDIT) ||
            authorizedActions.includes(AuthorizedActions.EDIT_SCORE)
        );
    }
    return false;
};

/**
 * Make sure every option in the multiple select question should have defined answer ID (unselected or selected)
 * @param answers
 * @param state
 */
const sanitizeMultipleSelectQuestionAnswers = (
    state: RootState,
    answers: EvaluationScoringSet
) => {
    // iterate every questionGroup and question in state.qualityForm.data
    // if the question is multiple select question
    // then visit every item in the multipleSelectQuestionOptions
    // if that item's id is not visible in the answers
    // then append it
    const questionGroups = state.qualityForm.data.questionGroups || {};
    // Iterate through each question group
    Object.values(questionGroups).forEach((questionGroup) => {
        const questionGroupId = questionGroup.id;
        // Find the corresponding question group score in answers
        const questionGroupScore = answers.questionGroupScores?.find(
            (qgs) => qgs.questionGroupId === questionGroupId
        );
        if (!questionGroupScore) return;
        // Iterate through each question in the question group
        questionGroup.questions.forEach((questionId) => {
            const question = state.qualityForm.data.questions?.[questionId];
            // Check if it's a multiple select question
            if (
                question?.type ===
                EvaluationQuestionTypeEnum.MultipleSelectQuestion
            ) {
                // Get the question score from answers
                const questionScore = questionGroupScore.questionScores?.find(
                    (qs) => qs.questionId === questionId
                );
                if (!questionScore) return;
                // Initialize multipleSelectQuestionOptionScores if it doesn't exist
                questionScore.multipleSelectQuestionOptionScores =
                    questionScore.multipleSelectQuestionOptionScores || [];

                const newModifiedOptionScores = [
                    ...questionScore.multipleSelectQuestionOptionScores,
                ];

                question.multipleSelectOptionQuestions?.forEach((optionId) => {
                    const existingScoreIndex =
                        newModifiedOptionScores.findIndex(
                            (score) => score.questionId === optionId
                        );

                    if (existingScoreIndex !== -1) {
                        const existingScore =
                            newModifiedOptionScores[existingScoreIndex];
                        if (!existingScore.answerId) {
                            const unselectedOption = selectAnswerOptionMutable(
                                state.qualityForm,
                                optionId,
                                true,
                                false
                            );
                            // Create new object instead of modifying existing
                            newModifiedOptionScores[existingScoreIndex] = {
                                ...existingScore,
                                answerId: unselectedOption?.id,
                            };
                        }
                    } else {
                        const unselectedOption = selectAnswerOptionMutable(
                            state.qualityForm,
                            optionId,
                            true,
                            false
                        );
                        newModifiedOptionScores.push({
                            questionId: optionId,
                            answerId: unselectedOption?.id,
                        } as EvaluationQuestionScore);
                    }
                });

                questionScore.multipleSelectQuestionOptionScores =
                    newModifiedOptionScores;
            }
        });
    });
};

export const selectAuthorizedActions = (state: RootState) =>
    state.scorable.data.scorable?.authorizedActions;

//TODO : need to make default scoring implementation consistent across all 3 forms.
export const loadScorable = createAsyncThunk<
    | EvaluationResponse
    | Survey
    | (LearningAssessment & { id?: string })
    | undefined,
    LoadFormArg,
    { state: RootState }
>(
    "scorable/loadScorable",
    async (args: LoadFormArg, { dispatch, getState, rejectWithValue }) => {
        const state: RootState = getState();
        const { formType } = args;
        const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
            state,
            FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
        );
        if (args.formType === FormType.EVALUATION) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            const isEvaluationAssigneeFeatureEnabled = selectFeatureToggle(
                state,
                FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
            );
            const isEvaluationFormDefaultAnsweringEnabled = selectFeatureToggle(
                state,
                FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
            );
            const isLensifyFTEnabled = selectFeatureToggle(
                state,
                FeatureToggle.LENSIFY_AISCORING
            );

            const expandList = ["evaluationForm", "evaluator", "agent"];
            if (isEvaluationAssigneeFeatureEnabled) {
                expandList.push("assignee");
            }
            expandList.push("versionHistory");
            return qualityApi
                .getQualityConversationEvaluation({
                    ...args,
                    expand: expandList.join(","),
                })
                .then(({ evaluationForm, ...rest }) => {
                    if (evaluationForm && evaluationForm.questionGroups) {
                        dispatch(
                            loadFormFulfilledActionCreator(
                                evaluationForm as EvaluationFormResponse,
                                formType
                            )
                        );
                    }
                    // TODO: when cleaning the FT, just remove isSystemMarkedNaFeatureEnabled
                    const canResetMarkedNABasedOnSystemMarkedNA =
                        isSystemMarkedNaFeatureEnabled &&
                        canEditScore(rest.authorizedActions);
                    if (
                        isExpectedEvaluationStatusInResponse(
                            rest,
                            EvaluationResponseStatusEnum.PENDING
                        )
                    ) {
                        const automatedScoringAnswers =
                            getDefaultAutomatedScoringAnswers(
                                isLensifyFTEnabled,
                                rest.answers
                            );

                        if (!rest.answers) {
                            rest.answers = getEmptyScoreSet(evaluationForm);
                        } else if (
                            isEvaluationAssigneeFeatureEnabled &&
                            rest.answers.privateComments &&
                            !(
                                (rest.answers.questionGroupScores?.length ??
                                    0) > 0
                            )
                        ) {
                            rest.answers = {
                                ...rest.answers,
                                questionGroupScores:
                                    getEmptyScoreSet(evaluationForm)
                                        .questionGroupScores,
                            };
                        }
                        let defaultAnswers = {};
                        // This condition (allGroupsHaveDefaultAnswersTo) check is to support the case
                        // where users still use evaluation forms that were published
                        // before the new default answers feature release.
                        // If a form is created or updated after the feature release,
                        // it is expected that all groups have defaultAnswersTo defined.
                        const allGroupsHaveDefaultAnswersTo =
                            evaluationForm?.questionGroups?.every(
                                (questionGroup) =>
                                    questionGroup.defaultAnswersTo !== undefined
                            );
                        if (
                            isEvaluationFormDefaultAnsweringEnabled &&
                            allGroupsHaveDefaultAnswersTo
                        ) {
                            const newDefaultAnswers = evaluationForm
                                ? getDefaultAnswers(evaluationForm)
                                : {};
                            defaultAnswers = {
                                ...newDefaultAnswers,
                                ...automatedScoringAnswers,
                            };
                        } else {
                            const highestAnswers = evaluationForm
                                ? getDefaultHighestScoreAnswers(evaluationForm)
                                : {};
                            const NAAnswers = evaluationForm
                                ? getDefaultNAAnswers(evaluationForm)
                                : {};
                            defaultAnswers = {
                                ...highestAnswers,
                                ...NAAnswers,
                                ...automatedScoringAnswers,
                            };
                        }

                        setDefaultAnswers(
                            defaultAnswers,
                            rest.answers,
                            args.formType,
                            canResetMarkedNABasedOnSystemMarkedNA
                        );
                    }

                    // For an evaluation in progress
                    setAnswersForInProgressEvaluation(
                        isLensifyFTEnabled,
                        rest,
                        canResetMarkedNABasedOnSystemMarkedNA
                    );

                    if (isSystemMarkedNaFeatureEnabled) {
                        const isFinishedEvaluation =
                            isExpectedEvaluationStatusInResponse(
                                rest,
                                EvaluationResponseStatusEnum.FINISHED
                            );
                        const isInReviewEvaluation =
                            isExpectedEvaluationStatusInResponse(
                                rest,
                                EvaluationResponseStatusEnum.INREVIEW
                            );
                        if (isFinishedEvaluation || isInReviewEvaluation) {
                            resetMarkedNAForHiddenQuestionsInEvaluation(
                                rest,
                                canResetMarkedNABasedOnSystemMarkedNA
                            );
                        }
                    }

                    return rest;
                });
        } else if (args.formType === FormType.SURVEY) {
            const { surveyId, surveyUrl } = args;
            const qualityApi = new QualityApi(API_CONFIGURATION);
            if (surveyId !== undefined) {
                return qualityApi
                    .getQualitySurvey({
                        surveyId,
                    })
                    .then(({ surveyForm, ...rest }) => {
                        if (surveyForm) {
                            dispatch(
                                loadFormFulfilledActionCreator(
                                    surveyForm,
                                    formType
                                )
                            );
                        }
                        return rest;
                    });
            } else if (surveyUrl !== undefined) {
                return qualityApi
                    .getQualitySurveysScorable({
                        customerSurveyUrl: surveyUrl,
                    })
                    .then((survey: ScorableSurvey) => {
                        const { surveyForm, ...rest } = SurveyFromJSON(survey);
                        if (!rest.answers) {
                            rest.answers = getEmptyScoreSet(surveyForm);
                            const defaultFreeTextAnswer = surveyForm
                                ? getDefaultFreeTextAnswer(surveyForm)
                                : {};
                            // isSystemMarkedNaFeatureEnabled should always be false here for scorable surveys
                            // so passing false.
                            setDefaultAnswers(
                                defaultFreeTextAnswer,
                                rest.answers,
                                args.formType,
                                false
                            );
                        }
                        if (surveyForm) {
                            dispatch(
                                loadFormFulfilledActionCreator(
                                    surveyForm,
                                    formType
                                )
                            );
                        }
                        return rest;
                    });
            } else {
                return rejectWithValue(
                    new Error("Neither survey url nor survey id are provided.")
                );
            }
        } else if (formType === FormType.ASSESSMENT) {
            const { assessmentForm, assessment } = args.assignmentData;
            if (
                assessment &&
                assessment.status === LearningAssessmentStatusEnum.Pending
            ) {
                assessment.answers = getDefaultSelectedAnswers(
                    assessmentForm,
                    FormType.ASSESSMENT
                );
            }
            if (isSystemMarkedNaFeatureEnabled) {
                setAnswersForAssessmentInProgress(
                    assessment,
                    isSystemMarkedNaFeatureEnabled
                );
            }

            if (assessmentForm) {
                assessmentForm.reviewAssessmentResults =
                    args.assignmentData.module?.reviewAssessmentResults;
                assessmentForm.assignee = args.assignmentData.user?.id;
                dispatch(
                    loadFormFulfilledActionCreator(assessmentForm, formType)
                );
            }
            return {
                id: assessment?.assessmentId,
                ...(assessment || {
                    answers: getDefaultSelectedAnswers(
                        assessmentForm,
                        FormType.ASSESSMENT
                    ),
                }),
            };
        } else {
            throw new Error(`Cannot load formType: ${formType}`);
        }
    }
);

export const getPreviewScores = createAsyncThunk<
    PreviewScoreResponse,
    {
        isScorableFinished: boolean;
        isInitialLoad?: boolean;
    },
    { state: RootState }
>(
    "scorable/getPreviewScores",
    async ({ isScorableFinished, isInitialLoad = false }, { getState }) => {
        const state: RootState = getState();

        if (!selectIsScorableValid(state) && isScorableFinished) {
            throw new Error("Scorable is not valid.");
        }

        const denormalizedForm = getDenormalizedQualityForm(state);
        const formType = selectFormType(state);
        const { scorable, ...rest } = state.scorable.data;
        const formId = denormalizedForm?.id ?? "";
        const data = denormalize(formId, scorableSchema, {
            scorables: {
                [formId]: scorable,
            },
            ...rest,
        });
        const answers = data.answers;

        const isMultipleSelectFTEnabled = selectFeatureToggle(
            state,
            FeatureToggle.MULTIPLE_SELECT
        );

        if (isInitialLoad) {
            // answers here are default answers from getDefaultSelectedAnswers in previewLoadScorable
            answers.questionGroupScores.forEach(
                (questionGroupScore: QuestionGroupScore) => {
                    questionGroupScore.questionScores = Object.values(
                        questionGroupScore.questionScores ?? []
                    ).filter(
                        (questionScore) =>
                            questionScore.answerId ||
                            questionScore.markedNA ||
                            (isMultipleSelectFTEnabled &&
                                questionScore.multipleSelectQuestionOptionScores)
                    );
                }
            );
        }

        if (!isScorableFinished) {
            answers.questionGroupScores.forEach(
                (questionGroupScore: QuestionGroupScore) => {
                    // include all visible questions
                    const visibleQuestionIds = selectVisibleQuestionIds(
                        state,
                        questionGroupScore.questionGroupId!
                    );
                    questionGroupScore.questionScores = visibleQuestionIds.map(
                        (questionId) =>
                            state.scorable.data.questionScores[questionId] as
                                | EvaluationQuestionScore
                                | SurveyQuestionScore
                                | AssessmentQuestionScore
                    );
                }
            );
        }

        const visibleQuestionGroupIds = selectVisibleQuestionGroupIds(state);

        // todo: handle the fatal question properly

        if (formType === FormType.EVALUATION) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            return qualityApi
                .postQualityEvaluationsScoring({
                    body: {
                        evaluationForm: denormalizedForm as EvaluationForm,
                        answers,
                    },
                })
                .then((answers) => ({
                    answers,
                    status: isScorableFinished
                        ? EvaluationStatusEnum.FINISHED
                        : EvaluationStatusEnum.INPROGRESS,
                    visibleQuestionGroupIds,
                }));
        } else if (formType === FormType.SURVEY) {
            // we don't send npsScore to the scoring API
            answers.npsScore = undefined;

            //TODO : remove the IF case all together once micro frontend lib is updated and is able to send published flag in the request body ('surveyForm: {published:true}').
            if ((denormalizedForm as SurveyForm)?.disabled) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return apiCore.v2
                    .post("/quality/surveys/scoring", {
                        surveyForm: denormalizedForm,
                        answers,
                    })
                    .then((answers: any) => ({
                        answers,
                        status: isScorableFinished
                            ? SurveyStatusEnum.Finished
                            : SurveyStatusEnum.InProgress,
                        visibleQuestionGroupIds,
                    }));
            } else {
                const qualityApi = new QualityApi(API_CONFIGURATION);
                return qualityApi
                    .postQualitySurveysScoring({
                        body: {
                            surveyForm: denormalizedForm as SurveyForm,
                            answers,
                        },
                    })
                    .then((answers) => ({
                        answers,
                        status: isScorableFinished
                            ? SurveyStatusEnum.Finished
                            : SurveyStatusEnum.InProgress,
                        visibleQuestionGroupIds,
                    }));
            }
        } else if (formType === FormType.ASSESSMENT) {
            const learningApi = new LearningApi(API_CONFIGURATION);
            return learningApi
                .postLearningAssessmentsScoring({
                    body: {
                        assessmentForm: denormalizedForm as AssessmentForm,
                        answers,
                    },
                })
                .then((answers) => ({
                    answers,
                    status: isScorableFinished
                        ? LearningAssessmentStatusEnum.Finished
                        : LearningAssessmentStatusEnum.InProgress,
                    visibleQuestionGroupIds,
                }));
        } else {
            throw new Error("Form Type is not supported.");
        }
    }
);

// TODO : remove ' expand: "evaluationForm" ' option from save & submit scorable async thunks once the cloud-frontend lib is updated to handle partial evaluation form in api response.
export const submitScorableBySupervisor = createAsyncThunk<
    Promise<EvaluationResponse | RejectedActionFromAsyncThunk<any> | undefined>,
    {
        currentAssignee?: {
            id: string | undefined;
        };
        releaseDate?: Date;
        rescoreMode?: boolean;
        declinedReview?: boolean;
        dispute?: boolean;
        isSubmitAndAssign?: boolean;
    },
    { state: RootState }
>(
    "scorable/submitScorableBySupervisor",
    async (
        {
            currentAssignee,
            releaseDate,
            rescoreMode,
            declinedReview,
            dispute,
            isSubmitAndAssign,
        },
        { getState, rejectWithValue }
    ) => {
        const state = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.EVALUATION) {
            const evaluationId = state.scorable.data.scorable?.id;
            const conversationId =
                state.scorable.data.scorable?.conversation?.id;
            if (evaluationId && conversationId) {
                if (!selectIsScorableValid(state)) {
                    throw rejectWithValue(new Error("Scorable is not valid."));
                }
                const qualityApi = new QualityApi(API_CONFIGURATION);
                const { scorable, ...rest } = state.scorable.data;

                const data = denormalize(evaluationId, scorableSchema, {
                    scorables: {
                        [evaluationId]: scorable,
                    },
                    ...rest,
                });

                sanitizeAnswers(data.answers, state);

                const isMultipleSelectFTEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.MULTIPLE_SELECT
                );

                // add some validation step around the multiple select question
                if (isMultipleSelectFTEnabled) {
                    sanitizeMultipleSelectQuestionAnswers(state, data.answers);
                }

                //clearing scores for hidden questions
                data.answers.questionGroupScores.forEach(
                    (questionGroupScore: QuestionGroupScore) => {
                        const visibleQuestionIds = new Set(
                            selectVisibleQuestionIds(
                                state,
                                questionGroupScore?.questionGroupId ?? ""
                            ) ?? []
                        );
                        questionGroupScore.questionScores?.forEach(
                            (questionScore) => {
                                if (
                                    !visibleQuestionIds?.has(
                                        questionScore?.questionId ?? ""
                                    )
                                ) {
                                    questionScore.answerId = "";
                                    questionScore.markedNA = false;
                                }
                            }
                        );
                    }
                );

                const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
                );

                const isEvaluationOverviewDeprecationFeatureEnabled =
                    selectFeatureToggle(
                        state,
                        FeatureToggle.EVALUATION_OVERVIEW_DEPRECATION
                    );

                const authorizedActions = selectAuthorizedActions(state);

                const hasReleasePermissions =
                    selectHasReleasePermissions(state);
                if (hasReleasePermissions) {
                    data.neverRelease = releaseDate ? false : true;
                    data.releaseDate = releaseDate ? releaseDate : undefined;
                } else {
                    // use current release date
                    data.neverRelease =
                        state.scorable.data.scorable?.neverRelease;
                    data.releaseDate =
                        state.scorable.data.scorable?.releaseDate;
                }

                // Toggling agentHasRead flag when evaluation is submitted after rescoring
                if (
                    (rescoreMode === true || declinedReview === true) &&
                    data.agentHasRead === true
                ) {
                    data.agentHasRead = false;
                }

                const expandList =
                    dispute || declinedReview
                        ? ["evaluationForm", "agent", "assignee"]
                        : ["evaluationForm"];

                expandList.push("versionHistory");

                if (
                    currentAssignee ||
                    dispute ||
                    declinedReview ||
                    isEvaluationOverviewDeprecationFeatureEnabled
                ) {
                    expandList.push("evaluator");
                }

                // Do not update release date or answers if dispute or dispute decline
                if (declinedReview || dispute) {
                    delete data.neverRelease;
                    delete data.releaseDate;
                    delete data.answers;
                }

                // TODO: Submit and assign conversation using qualityApi once public api swagger is updated
                // currentAssignee would only be defined when submit and assign option is chosen
                if (currentAssignee) {
                    return apiCore.v2
                        .put(
                            `/quality/conversations/${conversationId}/evaluations/${evaluationId}`,
                            {
                                ...data,
                                status: EvaluationStatusEnum.FINISHED,
                                published: true,
                                assignee: currentAssignee,
                                retractedEvaluation: undefined,
                            },
                            {
                                expand: expandList.join(","),
                            }
                        )
                        .then(function (body) {
                            body?.versionHistory?.entities?.forEach(
                                (entity) => {
                                    if (entity.submittedDate) {
                                        entity.submittedDate = new Date(
                                            entity.submittedDate
                                        );
                                    }
                                }
                            );
                            if (isSystemMarkedNaFeatureEnabled) {
                                resetMarkedNAForHiddenQuestionsInEvaluation(
                                    body,
                                    isSystemMarkedNaFeatureEnabled &&
                                        canEditScore(authorizedActions)
                                );
                            }

                            return {
                                ...body,
                                releaseDate:
                                    body.releaseDate &&
                                    new Date(body.releaseDate),
                                assignedDate:
                                    body.assignedDate &&
                                    new Date(body.assignedDate),
                                changedDate:
                                    body.changedDate &&
                                    new Date(body.changedDate),
                                revisionCreatedDate:
                                    body.revisionCreatedDate &&
                                    new Date(body.revisionCreatedDate),
                                conversationDate:
                                    body.conversationDate &&
                                    new Date(body.conversationDate),
                                conversationEndDate:
                                    body.conversationEndDate &&
                                    new Date(body.conversationEndDate),
                                dateAssigneeChanged:
                                    body.dateAssigneeChanged &&
                                    new Date(body.dateAssigneeChanged),
                                isSubmitAndAssign,
                            };
                        });
                } else {
                    return qualityApi
                        .putQualityConversationEvaluation({
                            conversationId,
                            evaluationId,
                            body: {
                                ...data,
                                status: dispute
                                    ? EvaluationStatusEnum.INREVIEW
                                    : EvaluationStatusEnum.FINISHED,
                                published: true,
                                declinedReview: declinedReview,
                                retractedEvaluation: undefined,
                                assignee: undefined,
                                assigned: undefined,
                            },
                            expand: expandList.join(","),
                        })
                        .then((evaluationResponse) => {
                            if (isSystemMarkedNaFeatureEnabled) {
                                resetMarkedNAForHiddenQuestionsInEvaluation(
                                    evaluationResponse,
                                    isSystemMarkedNaFeatureEnabled &&
                                        canEditScore(authorizedActions)
                                );
                            }

                            return evaluationResponse;
                        });
                }
            }
            return rejectWithValue(
                new Error("No evaluationId or conversationId.")
            );
        }
    }
);

export const loadAiScoringAnswers = createAsyncThunk<
    Promise<EvaluationResponse | RejectedActionFromAsyncThunk<any> | undefined>,
    void,
    { state: RootState }
>(
    "scorable/loadAiScoringAnswers",
    async (arg, { getState, rejectWithValue }) => {
        const state = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.EVALUATION) {
            const evaluationId = state.scorable.data.scorable?.id;
            const conversationId =
                state.scorable.data.scorable?.conversation?.id;
            if (evaluationId && conversationId) {
                const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
                );
                const isLensifyFTEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.LENSIFY_AISCORING
                );
                const qualityApi = new QualityApi(API_CONFIGURATION);
                return qualityApi
                    .getQualityConversationEvaluation({
                        conversationId,
                        evaluationId,
                    })
                    .then(({ evaluationForm, ...scorable }) => {
                        if (
                            scorable.answers &&
                            state.scorable.data.questionScores
                        ) {
                            updateAiScoringAnswers(
                                scorable.answers,
                                state.scorable.data.questionScores,
                                isSystemMarkedNaFeatureEnabled,
                                isLensifyFTEnabled
                            );
                        }

                        return scorable;
                    });
            }
            return rejectWithValue(
                new Error("No evaluationId or conversationId.")
            );
        }
        return rejectWithValue(new Error("formType is not Evaluation"));
    }
);

export const createCalibration = createAsyncThunk<
    Calibration | undefined,
    undefined,
    { state: RootState }
>("scorable/createCalibration", async (arg, { getState, rejectWithValue }) => {
    const state = getState();
    const evaluation = state.scorable.data.scorable;
    const { form } = state.qualityForm.data;
    const evaluationId = state.scorable.data.scorable?.id;
    const conversationId = state.scorable.data.scorable?.conversation?.id;
    const evaluators = state.scorable.data.scorable?.calibration?.evaluators;
    if (evaluationId && conversationId) {
        const calibration = {
            calibrator: { id: authService.principalId() },
            agent: { id: evaluation?.agent?.id },
            conversation: { id: conversationId },
            evaluationForm: {
                id: form?.id,
                name: form?.name ?? "",
                published: form?.published,
            },
            evaluators,
            scoringIndex: {
                id: evaluationId,
                conversation: { id: conversationId },
                evaluationForm: {
                    id: form?.id,
                    name: form?.name ?? "",
                    published: form?.published,
                },
                evaluator: { id: evaluation?.evaluator?.id },
            },
        };
        try {
            // Todo: update the swagger docs to match the expected data input
            // const qualityApi = new QualityApi();
            // return qualityApi.postQualityCalibrations({
            //     body: calibration,
            // });
            const value = await qualityManagementService.createCalibration(
                calibration
            );
            return value;
        } catch (error) {
            if (
                error instanceof HttpError &&
                error &&
                error.details &&
                "response" in error.details
            )
                return rejectWithValue(error?.details.response?.message);
        }
    }
    return rejectWithValue(new Error("No evaluationId or conversationId."));
});

export const saveScorableSurvey = createAsyncThunk<
    Promise<ScorableSurvey | RejectedActionFromAsyncThunk<any> | undefined>,
    string,
    { state: RootState }
>(
    "scorable/saveScorableSurvey",
    async (surveyUrl, { getState, rejectWithValue }) => {
        const state: RootState = getState();
        // Prevents saving a survey form when the text limit is exceeded
        if (!selectAreSurveyAnswersUnderLimit(state)) {
            throw new Error("Answer exceeded character limit.");
        }
        const denormalizedForm = getDenormalizedQualityForm(
            state
        ) as SurveyForm;
        const formType = selectFormType(state);
        const { scorable, ...rest } = state.scorable.data;
        const surveyId = denormalizedForm?.id ?? "";
        const data = denormalize(surveyId, scorableSchema, {
            scorables: {
                [surveyId]: scorable,
            },
            ...rest,
        });

        sanitizeAnswers(data.answers, state);
        const answers = data.answers;
        if (formType === FormType.SURVEY) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            try {
                const response = await qualityApi.putQualitySurveysScorable({
                    customerSurveyUrl: surveyUrl,
                    body: {
                        surveyForm: denormalizedForm,
                        answers,
                        status: ScorableSurveyStatusEnum.InProgress,
                    },
                });
                return response;
            } catch (e: any) {
                const errorResponse = await e.json();
                return rejectWithValue(new Error(errorResponse.code));
            }
        }
        throw new Error("Form Type is not supported.");
    }
);

export const submitScorableSurvey = createAsyncThunk<
    Promise<ScorableSurvey | RejectedActionFromAsyncThunk<any> | undefined>,
    string,
    { state: RootState }
>(
    "scorable/submitScorableSurvey",
    async (surveyUrl, { getState, rejectWithValue }) => {
        const state: RootState = getState();
        const denormalizedForm = getDenormalizedQualityForm(
            state
        ) as SurveyForm;
        const formType = selectFormType(state);
        const { scorable, ...rest } = state.scorable.data;
        const surveyId = denormalizedForm?.id ?? "";
        const data = denormalize(surveyId, scorableSchema, {
            scorables: {
                [surveyId]: scorable,
            },
            ...rest,
        });

        sanitizeAnswers(data.answers, state);

        //clearing scores for hidden questions. Since its a survey and can have
        // Also, setting freeTextAnswer and npsTextAnswer as undefined for hidden questions.
        data.answers?.questionGroupScores?.forEach(
            (questionGroupScore: SurveyQuestionGroupScore) => {
                const visibleQuestionIds = new Set(
                    selectVisibleQuestionIds(
                        state,
                        questionGroupScore?.questionGroupId ?? ""
                    ) ?? []
                );
                questionGroupScore.questionScores?.forEach((questionScore) => {
                    if (
                        !visibleQuestionIds?.has(
                            questionScore?.questionId ?? ""
                        )
                    ) {
                        questionScore.answerId = "";
                        questionScore.markedNA = false;
                        questionScore.npsScore = undefined;
                        questionScore.freeTextAnswer = undefined;
                        questionScore.npsTextAnswer = undefined;
                    }
                });
            }
        );

        if (!selectIsScorableValid(state)) {
            throw new Error("Scorable is not valid.");
        } else if (formType === FormType.SURVEY) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            try {
                const response = await qualityApi.putQualitySurveysScorable({
                    customerSurveyUrl: surveyUrl,
                    body: {
                        ...data,
                        surveyForm: denormalizedForm,
                        status: ScorableSurveyStatusEnum.Finished,
                        retractedEvaluation: undefined,
                    },
                });
                return response;
            } catch (e: any) {
                const errorResponse = await e.json();
                return rejectWithValue(new Error(errorResponse.code));
            }
        }
        throw new Error("Form Type is not supported.");
    }
);

export const previewLoadScorable =
    () => (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const formType = selectFormType(state);
        const denormalizedForm = getDenormalizedQualityForm(state);
        const id = denormalizedForm?.id ?? "";
        // Check feature toggle for default answering
        const isDefaultAnsweringEnabled = selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
        );
        const answers = getDefaultSelectedAnswers(
            denormalizedForm,
            formType,
            isDefaultAnsweringEnabled
        );

        const conversationId =
            state.scorable.data.scorable?.conversation?.id ?? "";

        if (denormalizedForm && formType) {
            const metaArgs =
                formType === FormType.ASSESSMENT
                    ? {
                          formType: formType,
                          assignmentId: id,
                      }
                    : formType === FormType.SURVEY
                    ? {
                          formType: formType,
                          surveyId: id,
                      }
                    : {
                          formType: formType,
                          conversationId: conversationId,
                          evaluationId: id!,
                      };
            dispatch(loadScorable.fulfilled({ id, answers }, "", metaArgs));
        }
    };

export const saveScorableBySupervisor = createAsyncThunk<
    EvaluationResponse | undefined,
    undefined,
    { state: RootState }
>(
    "scorable/saveScorableBySupervisor",
    async (arg, { getState, rejectWithValue }) => {
        const state: RootState = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.EVALUATION) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            const evaluationId = state.scorable.data.scorable?.id;
            const conversationId =
                state.scorable.data.scorable?.conversation?.id;
            if (evaluationId && conversationId) {
                const {
                    initialPrivateComment,
                    isEvaluationUpdated,
                    scorable,
                    ...rest
                } = state.scorable.data;
                const data = denormalize(evaluationId, scorableSchema, {
                    scorables: {
                        [evaluationId]: scorable,
                    },
                    ...rest,
                });

                sanitizeAnswers(data.answers, state);

                const isMultipleSelectFTEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.MULTIPLE_SELECT
                );

                // add some validation step around the multiple select question
                if (isMultipleSelectFTEnabled) {
                    sanitizeMultipleSelectQuestionAnswers(state, data.answers);
                }

                const { answers, status } = data;

                const isEvaluationAssigneeFeatureEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
                );
                const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
                );
                const isLensifyFTEnabled = selectFeatureToggle(
                    state,
                    FeatureToggle.LENSIFY_AISCORING
                );
                const hasAssignPermission = selectHasAssignPermission(state);
                const hasEditScoreOrEditPermissions =
                    selectHasEditScoreOrEditPermissions(state);
                const authorizedActions = selectAuthorizedActions(state);
                const isPrivateCommentsUpdated =
                    (initialPrivateComment ?? "").trim() !==
                    (answers.privateComments ?? "").trim();
                const expandList = ["evaluationForm", "evaluator", "agent"];
                expandList.push("versionHistory");
                if (isEvaluationAssigneeFeatureEnabled) {
                    // If only private comments editable or private comments changed in pending/in-progress
                    if (
                        (hasAssignPermission &&
                            !hasEditScoreOrEditPermissions) ||
                        (status !== EvaluationStatusEnum.FINISHED &&
                            !isEvaluationUpdated &&
                            isPrivateCommentsUpdated)
                    ) {
                        return qualityApi
                            .putQualityConversationEvaluation({
                                conversationId,
                                evaluationId,
                                body:
                                    // If private comments unchanged, call API w/ empty request
                                    isPrivateCommentsUpdated
                                        ? {
                                              answers: {
                                                  privateComments:
                                                      answers.privateComments,
                                              },
                                              status: status,
                                          }
                                        : {},
                                expand: expandList.join(","),
                            })
                            .then((evaluationResponse) => {
                                if (isSystemMarkedNaFeatureEnabled) {
                                    setAnswersForInProgressEvaluation(
                                        isLensifyFTEnabled,
                                        evaluationResponse,
                                        isSystemMarkedNaFeatureEnabled &&
                                            canEditScore(authorizedActions)
                                    );
                                }
                                return evaluationResponse;
                            });
                    } else {
                        return qualityApi
                            .putQualityConversationEvaluation({
                                conversationId,
                                evaluationId,
                                body: {
                                    answers: answers,
                                    status: EvaluationStatusEnum.INPROGRESS,
                                },
                                expand: expandList.join(","),
                            })
                            .then((evaluationResponse) => {
                                if (isSystemMarkedNaFeatureEnabled) {
                                    setAnswersForInProgressEvaluation(
                                        isLensifyFTEnabled,
                                        evaluationResponse,
                                        isSystemMarkedNaFeatureEnabled &&
                                            canEditScore(authorizedActions)
                                    );
                                }
                                return evaluationResponse;
                            });
                    }
                } else {
                    return qualityApi
                        .putQualityConversationEvaluation({
                            conversationId,
                            evaluationId,
                            body: {
                                answers: answers,
                                status: EvaluationStatusEnum.INPROGRESS,
                            },
                            expand: expandList.join(","),
                        })
                        .then((evaluationResponse) => {
                            if (isSystemMarkedNaFeatureEnabled) {
                                setAnswersForInProgressEvaluation(
                                    isLensifyFTEnabled,
                                    evaluationResponse,
                                    isSystemMarkedNaFeatureEnabled &&
                                        canEditScore(authorizedActions)
                                );
                            }
                            return evaluationResponse;
                        });
                }
            }
            return rejectWithValue(
                new Error("No conversationId or evaluationId found.")
            );
        }
        return rejectWithValue(new Error("No supported formType to save."));
    }
);

// Presently this function is used only by scorable assessments to extract appropriate answer data from RootState.
export const getAnswerData = (state: RootState) => {
    const questionGroupScores = selectQuestionGroupIds(state).map(
        (questionGroupId) => {
            const questionScores = selectQuestionIds(
                state,
                questionGroupId
            ).map((questionId) => {
                const questionScore = selectQuestionScore(state, questionId);
                let { answerId, comments } = questionScore ?? {};

                if (!answerId) {
                    answerId = "";
                }

                if (!comments) {
                    comments = "";
                }

                // unsetting markedNA if question doesn't support it.
                let markedNA = questionScore?.markedNA;
                if (
                    markedNA === true &&
                    selectQuestion(state, questionId)?.naEnabled === false
                ) {
                    markedNA = false;
                }

                const { freeTextAnswer } = questionScore ?? {};

                return {
                    questionId,
                    answerId,
                    comments,
                    freeTextAnswer,
                    markedNA,
                };
            });
            const markedNA = selectQuestionGroupMarkedNa(
                state,
                questionGroupId
            );
            return {
                questionGroupId,
                questionScores,
                markedNA,
            };
        }
    );
    const data = { questionGroupScores };

    //clearing scores for hidden questions
    // Also, setting freeTextAnswer as undefined for hidden questions.
    data?.questionGroupScores?.forEach(
        (questionGroupScore: AssessmentQuestionGroupScore) => {
            const visibleQuestionIds = new Set(
                selectVisibleQuestionIds(
                    state,
                    questionGroupScore?.questionGroupId ?? ""
                ) ?? []
            );
            questionGroupScore.questionScores?.forEach((questionScore) => {
                if (!visibleQuestionIds?.has(questionScore?.questionId ?? "")) {
                    questionScore.answerId = "";
                    questionScore.markedNA = false;
                    questionScore.freeTextAnswer = undefined;
                }
            });
        }
    );
    return data;
};

export const sendAssessmentAnswersData =
    () => (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.ASSESSMENT) {
            const answers = getAnswerData(state);
            const validationErrors = answers.questionGroupScores
                .filter(
                    (questionGroupScore) =>
                        !selectIsVisibleScorableQuestionGroupValid(
                            state,
                            questionGroupScore.questionGroupId
                        )
                )
                .flatMap(({ questionScores }) =>
                    questionScores.map(({ questionId }) => questionId)
                )
                .filter(
                    (questionId) =>
                        !selectIsScorableQuestionValid(state, questionId)
                )
                .map((questionId) => ({ question: { id: questionId } }));
            return embeddingService.sendFormAnswers({
                answers,
                validationErrors,
            });
        }
        throw new Error(
            `Unable to send assessment answers for FormType ${formType}`
        );
    };

export const saveScorableByAgent = createAsyncThunk<
    Promise<EvaluationResponse | RejectedActionFromAsyncThunk<any> | undefined>,
    { evaluationReviewed: boolean; dispute: boolean },
    { state: RootState }
>(
    "scorable/saveScorableByAgent",
    async ({ evaluationReviewed, dispute }, { getState, rejectWithValue }) => {
        const state = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.EVALUATION && evaluationReviewed) {
            const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
                state,
                FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
            );
            const evaluationId = state.scorable.data.scorable?.id;
            const conversationId =
                state.scorable.data.scorable?.conversation?.id;
            if (evaluationId && conversationId) {
                if (!selectIsScorableValid(state)) {
                    return rejectWithValue(new Error("Scorable is not valid."));
                }

                const qualityApi = new QualityApi(API_CONFIGURATION);
                const { scorable, ...rest } = state.scorable.data;

                const data = denormalize(evaluationId, scorableSchema, {
                    scorables: {
                        [evaluationId]: scorable,
                    },
                    ...rest,
                });
                data.agentHasRead = evaluationReviewed;

                if (isSystemMarkedNaFeatureEnabled) {
                    // We want to update the agentComments only.
                    data.answers = {
                        agentComments: data.answers.agentComments,
                    };
                }

                if (dispute) {
                    data.status = EvaluationStatusEnum.INREVIEW;
                }
                const willScorableBeFinished =
                    data.status === EvaluationStatusEnum.FINISHED;

                if (willScorableBeFinished) {
                    // TODO: Use qualityApi once public api swagger is updated
                    // Need to pass in assignee for finished state or else it will unassign
                    return apiCore.v2.put(
                        `/quality/conversations/${conversationId}/evaluations/${evaluationId}`,
                        {
                            ...data,
                            declinedReview: false,
                            retractedEvaluation: undefined,
                            assignee: scorable?.assignee,
                        },
                        {
                            expand: "evaluationForm",
                        }
                    );
                } else {
                    return qualityApi.putQualityConversationEvaluation({
                        conversationId,
                        evaluationId,
                        body: {
                            ...data,
                            declinedReview: false,
                            retractedEvaluation: undefined,
                        },
                        expand: "evaluationForm",
                    });
                }
            }
            return rejectWithValue(
                new Error("No evaluationId or conversationId.")
            );
        }
        return rejectWithValue(
            new Error(
                "No supported formType to save or evaluation reviewed checkbox not selected."
            )
        );
    }
);

const saveComment = createAsyncThunk<
    EvaluationResponse | undefined,
    { commentType: CommentType },
    { state: RootState }
>(
    "scorable/saveComment",
    async ({ commentType }, { dispatch, getState, rejectWithValue }) => {
        const state: RootState = getState();
        const formType = state.qualityForm.data.formType;
        const isScorableFinished = selectIsScorableFinished(state);

        if (formType !== FormType.EVALUATION) {
            return rejectWithValue(new Error("No supported formType to save."));
        }

        const evaluationId = state.scorable.data.scorable?.id;
        const conversationId = state.scorable.data.scorable?.conversation?.id;

        if (!evaluationId || !conversationId) {
            return rejectWithValue(
                new Error("No conversationId or evaluationId found.")
            );
        }

        const answers: any = {};
        let agentHasRead: boolean | undefined;
        switch (commentType) {
            case CommentType.REVIEW:
                answers.agentComments =
                    state.scorable.data.scorable?.answers?.agentComments;
                agentHasRead = state.scorable.data.scorable?.agentHasRead;

                break;
            case CommentType.EVALUATOR:
                answers.comments =
                    state.scorable.data.scorable?.answers?.comments;
                agentHasRead = false;

                break;
            case CommentType.PRIVATE:
                answers.privateComments =
                    state.scorable.data.scorable?.answers?.privateComments;
                agentHasRead = state.scorable.data.scorable?.agentHasRead;

                break;
            default:
                return rejectWithValue(new Error("No fields to update."));
        }

        // TODO: Use qualityApi once public api swagger is updated
        // Need to pass in assignee for finished state or else it will unassign
        return apiCore.v2
            .put(
                `/quality/conversations/${conversationId}/evaluations/${evaluationId}`,
                {
                    answers,
                    agentHasRead,
                    assignee: isScorableFinished
                        ? state.scorable.data.scorable?.assignee
                        : undefined,
                }
            )
            .then((evaluation) => {
                if (
                    state.scorable.data.scorable?.agentHasRead &&
                    !evaluation.agentHasRead
                ) {
                    dispatch(slice.actions.changeAgentReviewed());
                }

                return evaluation;
            });
    }
);

export const saveReviewComment = () => {
    return saveComment({
        commentType: CommentType.REVIEW,
    });
};

export const saveEvaluatorComment = () => {
    return saveComment({
        commentType: CommentType.EVALUATOR,
    });
};

export const savePrivateComment = () => {
    return saveComment({
        commentType: CommentType.PRIVATE,
    });
};

export const deleteScorable = createAsyncThunk<
    EvaluationResponse | undefined,
    undefined,
    { state: RootState }
>("scorable/deleteScorable", async (arg, { getState, rejectWithValue }) => {
    const state = getState();
    const formType = state.qualityForm.data.formType;
    if (formType === FormType.EVALUATION) {
        const conversationId = state.scorable.data.scorable?.conversation?.id;
        const evaluationId = state.scorable.data.scorable?.id;
        if (conversationId && evaluationId) {
            const qualityApi = new QualityApi(API_CONFIGURATION);
            const evaluation =
                await qualityApi.deleteQualityConversationEvaluation({
                    conversationId,
                    evaluationId,
                    expand: "evaluationForm",
                });
            return evaluation;
        }
        return rejectWithValue(
            new Error("No conversationId or evaluationId found")
        );
    }
    return rejectWithValue(new Error("No supported formType to delete"));
});

export const UNASSIGNED = "unassigned";

export const reassignScorable = createAsyncThunk<
    EvaluationResponse | undefined,
    User,
    { state: RootState }
>(
    "scorable/reassignScorable",
    async (assignee, { getState, rejectWithValue, dispatch }) => {
        const state = getState();
        const scorable = state.scorable.data.scorable;
        const formType = state.qualityForm.data.formType;
        const assign = !(
            assignee === undefined ||
            assignee?.id?.length === 0 ||
            assignee?.id === UNASSIGNED
        );
        const isEvaluationAssigneeFeatureEnabled = selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
        const isEvaluatorChangeFTEnabled = selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_CHANGE_TOGGLE
        );

        if (!scorable) {
            return rejectWithValue(new Error("No scorable found"));
        }

        if (formType === FormType.EVALUATION) {
            const conversationId = scorable.conversation?.id;
            const evaluationId = scorable.id;
            if (conversationId && evaluationId) {
                const isPrivateCommentsUpdated =
                    (state.scorable.data.initialPrivateComment ?? "").trim() !==
                    (scorable.answers?.privateComments ?? "").trim();

                const expandList =
                    isEvaluatorChangeFTEnabled &&
                    isEvaluationAssigneeFeatureEnabled
                        ? ["evaluator"]
                        : [""];

                // TODO: switch back to using typescript qualityApi
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return apiCore.v2
                    .put(
                        `/quality/conversations/${conversationId}/evaluations/${evaluationId}`,
                        {
                            assignee: assign
                                ? {
                                      id: assignee.id,
                                  }
                                : null,
                            assigned: assign,
                            answers:
                                // If private comments unchanged, do not pass in answers object
                                isPrivateCommentsUpdated
                                    ? {
                                          privateComments:
                                              scorable.answers?.privateComments,
                                      }
                                    : undefined,
                            agentHasRead: scorable.agentHasRead,
                            status: scorable.status ?? "",
                        },
                        {
                            expand: expandList.join(","),
                        }
                    )
                    .then((evaluation: any) => {
                        return evaluation;
                    })
                    .catch((err: any) => {
                        const error = err?.details?.response;
                        if (error !== undefined) {
                            dispatch(
                                scorableSliceActions.setUpdateEvaluationError({
                                    errorCode: error.code,
                                })
                            );
                            return rejectWithValue(new Error(error.code));
                        }
                        return rejectWithValue(
                            new Error("PUT Request Error While Assigning")
                        );
                    });
            }
            return rejectWithValue(
                new Error("No conversationId or evaluationId found")
            );
        }
        return rejectWithValue(new Error("formType is not Evaluation"));
    }
);

export const cancelEvaluationRescoring =
    () => (dispatch: Dispatch<any>, getState: () => RootState) => {
        const state = getState();
        const scorable = state.scorable.data.scorable;
        if (scorable?.id && scorable?.conversation?.id)
            dispatch(
                loadScorable({
                    formType: FormType.EVALUATION,
                    evaluationId: scorable.id,
                    conversationId: scorable.conversation.id,
                })
            );
    };

export const saveEvaluationReleaseDate = createAsyncThunk<
    EvaluationResponse | RejectedActionFromAsyncThunk<any> | undefined,
    { releaseDate: Date | undefined },
    { state: RootState }
>(
    "scorable/saveEvaluationReleaseDate",
    async ({ releaseDate }, { getState, rejectWithValue }) => {
        const state = getState();
        const scorable = state.scorable.data.scorable;
        const formType = state.qualityForm.data.formType;

        if (!scorable) {
            return rejectWithValue(new Error("No scorable found"));
        }

        if (formType !== FormType.EVALUATION) {
            return rejectWithValue(new Error("formType is not Evaluation"));
        }

        const conversationId = scorable.conversation?.id;
        const evaluationId = scorable.id;

        if (!conversationId || !evaluationId) {
            return rejectWithValue(
                new Error("No conversationId or evaluationId found")
            );
        }

        const neverRelease = !releaseDate;
        const isScorableFinished = selectIsScorableFinished(state);
        const supplyAssignee =
            isScorableFinished &&
            (!releaseDate || releaseDate.valueOf() > new Date().valueOf());

        // Need to pass in assignee for finished state or else it will unassign
        return apiCore.v2.put(
            `/quality/conversations/${conversationId}/evaluations/${evaluationId}`,
            {
                neverRelease,
                releaseDate,
                status: scorable.status,
                assignee: supplyAssignee ? scorable.assignee : undefined,
            }
        );
        // check if we need a catch block here
    }
);

const initialState: ScorableState = {
    data: {
        questionGroupScores: {},
        questionScores: {},
        hasScorableLoaded: false,
        isEvaluationUpdated: false,
    },
};

const setMetadataNames = (
    state: ScorableState,
    scorable: NormalizedScorable
) => {
    const oldAgentName = state.data.scorable?.agent?.name;
    if (!!scorable.agent && !scorable.agent.name && !!oldAgentName) {
        scorable.agent.name = oldAgentName;
    }
    const oldAssigneeName = state.data.scorable?.assignee?.name;
    if (!!scorable.assignee && !scorable.assignee.name && !!oldAssigneeName) {
        scorable.assignee.name = oldAssigneeName;
    }
    const oldEvaluatorName = state.data.scorable?.evaluator?.name;
    if (
        !!scorable.evaluator &&
        !scorable.evaluator.name &&
        !!oldEvaluatorName
    ) {
        scorable.evaluator.name = oldEvaluatorName;
    }
    const evaluationSourceName = state.data.scorable?.evaluationSource?.name;
    if (!!scorable.evaluationSource && !!evaluationSourceName) {
        scorable.evaluationSource.name = evaluationSourceName;
    }
};

const setAssigneeOrEvaluator = (user: User | undefined) => {
    // unassign when no assignee specified or unassign option chosen
    const isBeingUnassigned =
        user === undefined || user.id?.length === 0 || user?.id === UNASSIGNED;
    return isBeingUnassigned ? undefined : user;
};

export const sendAnswersDataOnFormDataChange =
    (callBack: () => void) =>
    (dispatch: Dispatch<any>, getState: () => RootState) => {
        callBack();
        const state = getState();
        const formType = state.qualityForm.data.formType;
        if (formType === FormType.ASSESSMENT) {
            dispatch(sendAssessmentAnswersData());
        }
    };

export const slice = createSlice({
    name: "scorable",
    initialState,
    reducers: {
        clearAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                naEnabled: boolean;
                isMultipleSelect?: boolean;
                multipleSelectOptionsIdMappings?: any;
            }>
        ) {
            const question = state.data.questionScores[
                payload.questionId
            ] as QuestionScore;
            if (question) {
                const isOptionalFreeText =
                    Boolean(question?.freeTextAnswer).valueOf() &&
                    payload.naEnabled;
                question.answerId = "";
                question.npsScore = undefined;
                question.npsTextAnswer = "";
                question.freeTextAnswer = "";
                // We should be setting markedNA false in every case EXCEPT when a free text is optional
                question.markedNA = isOptionalFreeText;
                question["answerSource"] = undefined;

                if (
                    payload.isMultipleSelect &&
                    payload.multipleSelectOptionsIdMappings
                ) {
                    // set all the answer options to be set with the unselected ID
                    const resettedMultipleSelectQuestionOptionScores =
                        [] as EvaluationQuestionScore[];
                    const originalQuestionOptionScores =
                        question.multipleSelectQuestionOptionScores;
                    for (const optionId in payload.multipleSelectOptionsIdMappings) {
                        const originalOptionScore = (
                            originalQuestionOptionScores ?? []
                        ).find((optionScore) => {
                            return optionScore.questionId === optionId;
                        });
                        const optionMapping =
                            payload.multipleSelectOptionsIdMappings[optionId];
                        resettedMultipleSelectQuestionOptionScores.push({
                            ...originalOptionScore,
                            answerId: optionMapping.Unselected,
                            questionId: optionId,
                        });
                    }
                    question.multipleSelectQuestionOptionScores =
                        resettedMultipleSelectQuestionOptionScores;
                }
            }
        },
        setAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string;
                questionId: string;
                answerId: string;
            }>
        ) {
            const questionGroupScore =
                state.data.questionGroupScores[payload.questionGroupId] ??
                ({} as QuestionGroupScore);

            const questionScores: string[] =
                (questionGroupScore.questionScores as string[]) ?? [];
            if (!questionScores?.includes(payload.questionId)) {
                questionScores?.push(payload.questionId);
            }

            const questionScore = {
                ...state.data.questionScores[payload.questionId],
                answerSource: AnswerSource.USER,
            } as QuestionScore;
            if (questionScore.markedNA) {
                questionScore.markedNA = false;
            }
            questionScore.answerId = payload.answerId;
            questionScore.questionId = payload.questionId;
            state.data.questionScores[payload.questionId] = questionScore;
        },
        changeQuestionNA(
            state,
            {
                payload,
            }: PayloadAction<{ questionGroupId: string; questionId: string }>
        ) {
            const questionGroupScore =
                state.data.questionGroupScores[payload.questionGroupId] ??
                ({} as QuestionGroupScore);

            const questionScores: string[] =
                (questionGroupScore.questionScores as string[]) ?? [];
            if (!questionScores.includes(payload.questionId)) {
                questionScores.push(payload.questionId);
            }

            const questionScore =
                state.data.questionScores[payload.questionId] ??
                ({ questionId: payload.questionId } as QuestionScore);

            // clear answerId when question goes from markedNA = false to true
            if (!questionScore.markedNA) {
                questionScore.answerId = "";
                questionScore["answerSource"] = AnswerSource.USER;
            } else {
                questionScore["answerSource"] = undefined;
            }
            questionScore.markedNA = !questionScore.markedNA;
            state.data.questionScores[payload.questionId] = questionScore;
        },
        changeGroupNA(state, { payload }: PayloadAction<string>) {
            const questionGroup = state.data.questionGroupScores[payload];
            if (questionGroup) {
                questionGroup.markedNA = !questionGroup.markedNA;
            }
        },
        setGroupNA(
            state,
            {
                payload,
            }: PayloadAction<{ questionGroupId: string; isMarkedNA: boolean }>
        ) {
            const questionGroup =
                state.data.questionGroupScores[payload.questionGroupId];
            if (questionGroup) {
                questionGroup.markedNA = payload.isMarkedNA;
            }
        },
        fillQuestionComments(
            state,
            {
                payload,
            }: PayloadAction<{ questionId: string; newComments: string }>
        ) {
            const questionScore = state.data.questionScores[payload.questionId];
            if (questionScore) {
                questionScore.comments = payload.newComments;
                state.data.questionScores[payload.questionId] = questionScore;
            }
        },
        fillEvaluatorComments(state, { payload }: PayloadAction<string>) {
            const answers = state.data.scorable?.answers;
            if (answers) {
                answers.comments = payload;
            }
        },
        changeAgentReviewed(state) {
            const scorable = state.data.scorable;
            if (scorable) {
                scorable.agentHasRead = !scorable.agentHasRead;
            }
        },
        fillReviewComments(state, { payload }: PayloadAction<string>) {
            const answers = state.data.scorable?.answers;
            if (answers) {
                answers.agentComments = payload;
            }
        },
        fillPrivateComments(state, { payload }: PayloadAction<string>) {
            const answers = state.data.scorable?.answers;
            if (answers) {
                answers.privateComments = payload;
            }
        },
        cancelPrivateComments(state) {
            const answers = state.data.scorable?.answers;
            if (answers) {
                answers.privateComments = state.data.initialPrivateComment;
            }
        },
        clearScorable(state) {
            state.data = {
                questionGroupScores: {},
                questionScores: {},
                hasScorableLoaded: false,
                isEvaluationUpdated: false,
            };
        },
        updateEvaluationStatusToInProgress(state) {
            const scorable = state.data.scorable;
            if (scorable && scorable.status) {
                scorable.status = EvaluationStatusEnum.INPROGRESS;
            }
        },
        setNpsScore(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string;
                questionId: string;
                value: number;
            }>
        ) {
            const questionGroupScore =
                state.data.questionGroupScores[payload.questionGroupId] ??
                ({} as QuestionGroupScore);

            const questionScores: string[] =
                (questionGroupScore.questionScores as string[]) ?? [];
            if (!questionScores?.includes(payload.questionId)) {
                questionScores?.push(payload.questionId);
            }

            const questionScore =
                state.data.questionScores[payload.questionId] ??
                ({ questionId: payload.questionId } as QuestionScore);
            questionScore.npsScore = payload.value;
            questionScore["answerSource"] = AnswerSource.USER;
            state.data.questionScores[payload.questionId] = questionScore;
        },
        setNpsTextAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                text: string;
            }>
        ) {
            const questionScore = state.data.questionScores[payload.questionId];
            if (questionScore) {
                questionScore.npsTextAnswer = payload.text;
                questionScore["answerSource"] = AnswerSource.USER;
                state.data.questionScores[payload.questionId] = questionScore;
            }
        },
        setFreeTextAnswer(
            state,
            {
                payload,
            }: PayloadAction<{
                questionId: string;
                text: string;
                naEnabled: boolean;
            }>
        ) {
            const questionScore = state.data.questionScores[payload.questionId];
            if (questionScore) {
                questionScore.freeTextAnswer = payload.text;
                if (
                    (!payload.text || payload.text.trim() === "") &&
                    payload.naEnabled
                ) {
                    questionScore.markedNA = true;
                } else if (questionScore.markedNA) {
                    questionScore.markedNA = false;
                }
                questionScore["answerSource"] = AnswerSource.USER;
                state.data.questionScores[payload.questionId] = questionScore;
            }
        },
        updateMultipleSelectAnswers(
            state,
            {
                payload,
            }: PayloadAction<{
                questionGroupId: string;
                questionId: string;
                checkboxAnswerId: string | undefined;
                dropdownAnswerIdList: string[] | undefined;
                answerIdsMapping: object | undefined;
            }>
        ) {
            const questionGroupScore =
                state.data.questionGroupScores[payload.questionGroupId] ??
                ({} as QuestionGroupScore);
            const questionScores: string[] =
                (questionGroupScore.questionScores as string[]) ?? [];
            if (!questionScores?.includes(payload.questionId)) {
                questionScores?.push(payload.questionId);
            }
            const questionScore = {
                ...state.data.questionScores[payload.questionId],
                answerSource: AnswerSource.USER,
            } as QuestionScore;
            if (questionScore.markedNA) {
                questionScore.markedNA = false;
            }
            // if it is from the dropdown
            const dropdownAnswerIdList = payload.dropdownAnswerIdList;
            // if it is from the checkbox
            const checkboxAnswerId = payload.checkboxAnswerId ?? "";
            // go through each answerId in answerIdsMapping
            // if each item (key) is in the selectedAnswerIds, then update its answerId to answerIdsMapping[key].Selected
            // otherwise, set its answerId as answerIdsMapping[key].Unselected
            // push the item to questionScore.multipleSelectOptionQuestionScores
            const answerIdsMapping = payload.answerIdsMapping;
            if (answerIdsMapping && dropdownAnswerIdList) {
                const multipleSelectOptionQuestionScores: EvaluationQuestionScore[] =
                    [];
                for (const key in answerIdsMapping) {
                    const answerId = dropdownAnswerIdList?.includes(key)
                        ? (answerIdsMapping as any)[key].Selected
                        : (answerIdsMapping as any)[key].Unselected;
                    // find the item with questionId === key in questionScore.multipleSelectQuestionOptionScores
                    const originalOptionScore =
                        questionScore.multipleSelectQuestionOptionScores?.find(
                            (optionScore: EvaluationQuestionScore) =>
                                optionScore.questionId === key
                        );
                    const multipleSelectOptionQuestionScore: EvaluationQuestionScore =
                        {
                            ...originalOptionScore,
                            answerId: answerId,
                            questionId: key,
                        };
                    multipleSelectOptionQuestionScores.push(
                        multipleSelectOptionQuestionScore
                    );
                }
                questionScore.multipleSelectQuestionOptionScores =
                    multipleSelectOptionQuestionScores;
            } else if (answerIdsMapping && checkboxAnswerId) {
                const originalOptionScore =
                    questionScore.multipleSelectQuestionOptionScores?.find(
                        (optionScore: EvaluationQuestionScore) =>
                            optionScore.questionId === checkboxAnswerId
                    );
                // Unselect the answer if it is already defined and has Selected Answer ID
                if (originalOptionScore) {
                    if (
                        originalOptionScore.answerId ===
                        answerIdsMapping[checkboxAnswerId].Selected
                    ) {
                        originalOptionScore.answerId =
                            answerIdsMapping[checkboxAnswerId].Unselected;
                    } else {
                        originalOptionScore.answerId =
                            answerIdsMapping[checkboxAnswerId].Selected;
                    }
                } else {
                    if (!questionScore.multipleSelectQuestionOptionScores) {
                        questionScore.multipleSelectQuestionOptionScores = [];
                    }
                    questionScore.multipleSelectQuestionOptionScores.push({
                        questionId: checkboxAnswerId,
                        answerId: answerIdsMapping[checkboxAnswerId].Selected,
                    } as EvaluationQuestionScore);
                }
            }
            questionScore.questionId = payload.questionId;
            state.data.questionScores[payload.questionId] = questionScore;
        },
        setCalibrationEvaluators(
            state,
            { payload }: PayloadAction<User[] | undefined>
        ) {
            const scorable = state.data.scorable;
            if (scorable)
                if (!scorable.calibration) {
                    scorable.calibration = { evaluators: payload };
                } else scorable.calibration.evaluators = payload;
        },
        clearCalibration(state) {
            const scorable = state.data.scorable;
            if (scorable) scorable.calibration = undefined;
        },
        clearCalibrationEvaluators(state) {
            const scorable = state.data.scorable;
            if (scorable)
                if (!scorable.calibration) {
                    scorable.calibration = { evaluators: undefined };
                } else scorable.calibration.evaluators = undefined;
        },
        setAssignee(state, { payload }: PayloadAction<User | undefined>) {
            const scorable = state.data.scorable;
            if (scorable) {
                scorable.assignee = setAssigneeOrEvaluator(payload);
            }
        },
        clearAssignee(state) {
            const scorable = state.data.scorable;
            if (scorable) {
                scorable.assignee = undefined;
            }
        },
        setEvaluator(state, { payload }: PayloadAction<User | undefined>) {
            const scorable = state.data.scorable;
            if (scorable) {
                scorable.evaluator = setAssigneeOrEvaluator(payload);
            }
        },
        setUpdateEvaluationError(
            state,
            { payload }: PayloadAction<{ errorCode?: string }>
        ) {
            state.data.updateEvaluationErrorCode = payload.errorCode;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(loadScorable.rejected, (state) => {
                state.data = {
                    questionGroupScores: {},
                    questionScores: {},
                    hasScorableLoaded: true,
                    isEvaluationUpdated: false,
                };
            })
            .addCase(submitScorableBySupervisor.rejected, () => {
                console.log("error: submit not succeeded");
            })
            .addCase(submitScorableSurvey.rejected, () => {
                console.log("error: submit not succeeded");
            })
            .addCase(saveScorableSurvey.rejected, () => {
                console.log("error: save not succeeded");
            })
            .addCase(saveScorableBySupervisor.rejected, () => {
                console.log("error: save not succeeded");
            })
            .addCase(saveScorableByAgent.rejected, () => {
                console.log("error: save not succeeded");
            })
            .addCase(saveComment.rejected, () => {
                console.log("error: save comment not succeeded");
            })
            .addCase(deleteScorable.rejected, () => {
                console.log("error: delete not succeeded");
            })
            .addCase(getPreviewScores.rejected, () => {
                console.log("error: preview submit not succeeded");
            })
            .addCase(createCalibration.rejected, (_, action) => {
                console.log(
                    "error: calibration creation not succeeded",
                    action.payload
                );
                throw new Error(action.payload as string);
            })
            .addCase(getPreviewScores.fulfilled, (state, { payload }) => {
                const normalizedData = normalize(payload, scorableSchema);
                const { scorables, ...rest } = normalizedData.entities;

                if (!scorables) {
                    return;
                }

                const existingQuestionScores = state.data.questionScores;

                // scoredQuestions from the payload
                const scoredQuestions = rest.questionScores;
                const scoredQuestionsKeys = Object.keys(scoredQuestions ?? {});

                const updatedQuestionScores: Record<
                    string,
                    QuestionScore | undefined
                > = {};

                Object.keys(existingQuestionScores ?? {}).forEach(
                    (questionScoreId: string) => {
                        //update existingQuestionScores
                        updatedQuestionScores[questionScoreId] =
                            scoredQuestionsKeys.includes(questionScoreId)
                                ? (scoredQuestions![
                                      questionScoreId
                                  ] as any as QuestionScore)
                                : existingQuestionScores[questionScoreId];
                    }
                );

                const updatedQuestionGroupScores =
                    rest.questionGroupScores as any as Record<
                        string,
                        NormalizedQuestionGroupScores | undefined
                    >;

                Object.keys(state.data.questionGroupScores ?? {})
                    .filter(
                        (questionGroupId) =>
                            !payload.visibleQuestionGroupIds?.includes(
                                questionGroupId
                            )
                    )
                    .forEach((questionGroupId) => {
                        // hidden questionGroups should have markedNA = false
                        updatedQuestionGroupScores[questionGroupId]!.markedNA =
                            false;
                    });

                state.data = {
                    questionGroupScores: updatedQuestionGroupScores,
                    questionScores: updatedQuestionScores,
                    scorable: scorables[normalizedData.result],
                    transcriptPhrases: state.data.transcriptPhrases,
                    hasScorableLoaded: false,
                    isEvaluationUpdated: false,
                    initialPrivateComment:
                        state.data.scorable?.answers?.privateComments,
                };
            })
            .addCase(
                saveEvaluationReleaseDate.fulfilled,
                (state, { payload }) => {
                    const normalizedData = normalize(payload, scorableSchema);
                    const { scorables } = normalizedData.entities;

                    if (scorables === undefined) {
                        return;
                    }
                    const scorable = scorables[normalizedData.result];
                    if (
                        state.data.scorable === undefined ||
                        scorable === undefined
                    ) {
                        return;
                    }
                    setMetadataNames(state, scorable);

                    state.data.scorable.releaseDate = scorable.releaseDate
                        ? new Date(scorable.releaseDate)
                        : undefined;
                    state.data.scorable.assignee = scorable.assignee;
                    state.data.scorable.neverRelease = scorable.neverRelease;
                    state.data.scorable.assigneeApplicable =
                        scorable.assigneeApplicable;
                }
            )
            .addCase(reassignScorable.fulfilled, (state, { payload }) => {
                const normalizedData = normalize(payload, scorableSchema);
                const { scorables } = normalizedData.entities;

                if (!scorables) {
                    state.data = {
                        questionGroupScores: {},
                        questionScores: {},
                        hasScorableLoaded: false,
                        isEvaluationUpdated: false,
                    };
                    return;
                }

                const scorable = scorables[normalizedData.result];
                state.data = {
                    ...state.data,
                    isEvaluationUpdated: false,
                    initialPrivateComment: scorable?.answers?.privateComments,
                    sideDrawerMode: SideDrawerMode.OFF,
                };
                if (
                    state.data.scorable &&
                    scorable.evaluator &&
                    scorable.evaluator.id &&
                    scorable.evaluator.name
                ) {
                    state.data.scorable.evaluator = scorable.evaluator;
                }
            })
            .addCase(saveComment.fulfilled, (state, { payload }) => {
                const normalizedData = normalize(payload, scorableSchema);
                const { scorables } = normalizedData.entities;

                if (!scorables) {
                    state.data = {
                        questionGroupScores: {},
                        questionScores: {},
                        hasScorableLoaded: false,
                        isEvaluationUpdated: false,
                    };
                    return;
                }

                const scorable = scorables[normalizedData.result];

                state.data = {
                    ...state.data,
                    isEvaluationUpdated: false,
                    initialPrivateComment: scorable?.answers?.privateComments,
                    sideDrawerMode: SideDrawerMode.OFF,
                };
            })
            .addCase(loadAiScoringAnswers.fulfilled, (state, { payload }) => {
                const normalizedData = normalize(payload, scorableSchema);
                // rest includes questionScores and questionGroupScores
                const { scorables, ...rest } = normalizedData.entities;

                if (!scorables) {
                    state.data = {
                        questionGroupScores: {},
                        questionScores: {},
                        hasScorableLoaded: false,
                        isEvaluationUpdated: false,
                    };
                    return;
                }

                const scorable = scorables[normalizedData.result];

                state.data = {
                    ...state.data,
                    ...rest,
                };
                if (state.data.scorable) {
                    state.data.scorable.aiScoring = scorable.aiScoring;
                }
            })
            .addMatcher(
                isAnyOf(
                    saveScorableBySupervisor.fulfilled,
                    submitScorableBySupervisor.fulfilled
                ),
                (state, { payload }) => {
                    const normalizedData = normalize(payload, scorableSchema);
                    const { scorables, ...rest } = normalizedData.entities;

                    if (!scorables) {
                        state.data = {
                            questionGroupScores: {},
                            questionScores: {},
                            hasScorableLoaded: false,
                            isEvaluationUpdated: false,
                        };
                        return;
                    }

                    const transcriptTopics =
                        scorables[normalizedData.result].answers
                            ?.transcriptTopics;

                    const groupedTranscriptTopics =
                        transcriptTopics &&
                        transcriptTopics.reduce((result, transcriptTopic) => {
                            const value = transcriptTopic.id!;
                            result[value] = (result[value] || []).concat(
                                transcriptTopic
                            );
                            return result;
                        }, {} as Record<string, TranscriptTopic[]>);
                    const scorable = scorables[normalizedData.result];
                    scorable.evaluationForm = undefined;
                    setMetadataNames(state, scorable);

                    if (
                        scorable?.status === EvaluationStatusEnum.PENDING &&
                        scorable?.answers === undefined
                    ) {
                        // API called in PENDING  state but no changes so do not update the redux store
                        return;
                    }

                    // If the evaluation id is different, then a new version of the evaluation was created and returned
                    if (scorable.id !== state.data.scorable?.id) {
                        state.data.isEvaluationDifferent = true;
                        state.data.isSubmitAndAssign =
                            payload?.["isSubmitAndAssign"] ?? false;
                        if (state.data.scorable) {
                            state.data.scorable.status =
                                EvaluationStatusEnum.RETRACTED;
                            state.data.scorable.id = scorable.id;
                        }

                        // checks if submit and assign option was pressed
                        if (
                            state.data.scorable?.assignee?.id ===
                                scorable.assignee?.id &&
                            scorable.status === EvaluationStatusEnum.FINISHED
                        ) {
                            state.data.sideDrawerMode = SideDrawerMode.ASSIGN;
                        }
                        delete payload?.["isSubmitAndAssign"];
                        return;
                    }

                    // if the evaluation is still in pending then only private comments were updated
                    // therefore, we should only update private comments in the state data
                    if (
                        scorable?.status === EvaluationStatusEnum.PENDING &&
                        state.data.scorable &&
                        state.data.scorable.answers &&
                        scorable?.answers?.privateComments
                    ) {
                        // if the evaluation is still in pending with answers then only private comments were updated
                        // therefore, we should only update private comments in the state data
                        state.data = {
                            ...state.data,
                            scorable: {
                                ...state.data.scorable,
                                answers: {
                                    ...state.data.scorable.answers,
                                    privateComments:
                                        scorable?.answers?.privateComments,
                                },
                            },
                            isEvaluationUpdated: false,
                            initialPrivateComment:
                                scorable?.answers?.privateComments,
                            sideDrawerMode: SideDrawerMode.OFF,
                        };
                    } else {
                        // TODO: When QM-14867 allows the PUT Evaluation API to return authorized actions,
                        // do not need to store the previous authorizedActions and can collapse this case with the case below
                        state.data = {
                            questionGroupScores: {},
                            questionScores: {},
                            scorable: {
                                ...scorable,
                                versionHistory: scorable.versionHistory,
                                authorizedActions:
                                    state.data.scorable?.authorizedActions,
                            },
                            transcriptPhrases: groupedTranscriptTopics,
                            ...rest,
                            hasScorableLoaded: true,
                            isEvaluationUpdated: false,
                            initialPrivateComment:
                                scorable?.answers?.privateComments,
                            sideDrawerMode: SideDrawerMode.OFF,
                        };
                    }
                }
            )
            .addMatcher(
                isAnyOf(
                    loadScorable.fulfilled,
                    saveScorableByAgent.fulfilled,
                    saveScorableSurvey.fulfilled,
                    submitScorableSurvey.fulfilled
                ),
                (state, { payload }) => {
                    const normalizedData = normalize(payload, scorableSchema);
                    const { scorables, ...rest } = normalizedData.entities;

                    if (!scorables) {
                        state.data = {
                            questionGroupScores: {},
                            questionScores: {},
                            hasScorableLoaded: false,
                            isEvaluationUpdated: false,
                        };
                        return;
                    }

                    const transcriptTopics =
                        scorables[normalizedData.result].answers
                            ?.transcriptTopics;

                    const groupedTranscriptTopics =
                        transcriptTopics &&
                        transcriptTopics.reduce((result, transcriptTopic) => {
                            const value = transcriptTopic.id!;
                            result[value] = (result[value] || []).concat(
                                transcriptTopic
                            );
                            return result;
                        }, {} as Record<string, TranscriptTopic[]>);
                    const scorable = scorables[normalizedData.result];
                    setMetadataNames(state, scorable);

                    state.data = {
                        questionGroupScores: {},
                        questionScores: {},
                        scorable,
                        transcriptPhrases: groupedTranscriptTopics,
                        ...rest,
                        hasScorableLoaded: true,
                        isEvaluationUpdated: false,
                        initialPrivateComment:
                            scorable?.answers?.privateComments,
                        sideDrawerMode: SideDrawerMode.OFF,
                    };
                }
            )
            .addMatcher(
                isAnyOf(
                    submitScorableBySupervisor.fulfilled,
                    saveScorableBySupervisor.fulfilled,
                    deleteScorable.fulfilled,
                    saveComment.fulfilled,
                    saveScorableByAgent.fulfilled,
                    createCalibration.fulfilled,
                    reassignScorable.fulfilled,
                    saveEvaluationReleaseDate.fulfilled,
                    submitScorableBySupervisor.rejected,
                    saveScorableBySupervisor.rejected,
                    deleteScorable.rejected,
                    saveComment.rejected,
                    saveScorableByAgent.rejected,
                    createCalibration.rejected,
                    reassignScorable.rejected,
                    saveEvaluationReleaseDate.rejected
                ),
                (state) => {
                    state.data.isOperationInProgress = false;
                }
            )
            .addMatcher(
                isAnyOf(
                    submitScorableBySupervisor.pending,
                    saveScorableBySupervisor.pending,
                    deleteScorable.pending,
                    saveComment.pending,
                    saveScorableByAgent.pending,
                    createCalibration.pending,
                    reassignScorable.pending,
                    saveEvaluationReleaseDate.pending
                ),
                (state) => {
                    state.data.isOperationInProgress = true;
                }
            )
            .addMatcher(
                (action) =>
                    action.type.match("^scorable/.*") &&
                    !action.type.match(".*/pending|fulfilled|rejected$") &&
                    (action.type.match(".*clearAnswer$") ||
                        action.type.match(".*setAnswer$") ||
                        action.type.match(".*changeQuestionNA$") ||
                        action.type.match(".*changeGroupNA$") ||
                        action.type.match(".*fillQuestionComments$") ||
                        action.type.match(".*fillEvaluatorComments$") ||
                        action.type.match(
                            ".*updateEvaluationStatusToInProgress$"
                        )),
                (state) => {
                    state.data.isEvaluationUpdated = true;
                }
            );
    },
});
export const scorableSliceActions = slice.actions;

export const selectIsDisputeEnabled = (state: RootState) => {
    const { disputesEnabled } = getEvaluationSettings(state) ?? {};

    return disputesEnabled;
};

export const selectIsDisputeLimitReached = (state: RootState) => {
    const disputeCount = selectDisputeCount(state) ?? 0;
    const { disputesAllowedPerEvaluation } = getEvaluationSettings(state) ?? {};

    return disputeCount >= disputesAllowedPerEvaluation;
};

export const selectScorable = (state: RootState) =>
    state.scorable.data.scorable;

export const selectVersionHistory = (state: RootState) =>
    state.scorable.data.scorable?.versionHistory;

// Sort in descending order by version number
export const selectVersionEntitiesSorted = (state: RootState) =>
    [...(state.scorable.data.scorable?.versionHistory?.entities ?? [])].sort(
        (a, b) => (b.version ?? 0) - (a.version ?? 0)
    );

export const selectIsPrivateCommentUpdated = (state: RootState) =>
    !state.scorable.data.isEvaluationUpdated &&
    state.scorable.data.scorable?.answers?.privateComments &&
    state.scorable.data.initialPrivateComment !==
        state.scorable.data.scorable?.answers?.privateComments;

export const selectIsScorableFinished = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return (
        status === EvaluationStatusEnum.FINISHED ||
        status === SurveyStatusEnum.Finished ||
        status === LearningAssessmentStatusEnum.Finished
    );
};

export const selectIsScorableComplete = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return (
        status === EvaluationStatusEnum.FINISHED ||
        status === EvaluationStatusEnum.INREVIEW ||
        status === EvaluationStatusEnum.RETRACTED ||
        status === SurveyStatusEnum.Finished ||
        status === LearningAssessmentStatusEnum.Finished
    );
};

export const selectIsScorableInReview = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return status === EvaluationStatusEnum.INREVIEW;
};

export const selectIsScorableRetracted = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return status === EvaluationStatusEnum.RETRACTED;
};

export const selectDisputeCount = (state: RootState) => {
    const scorable = selectScorable(state);
    return scorable?.disputeCount;
};

export const selectIsSurveyIncomplete = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return (
        status &&
        (status === SurveyStatusEnum.InProgress ||
            status === SurveyStatusEnum.Pending ||
            status === SurveyStatusEnum.Sent)
    );
};

export const selectIsEvaluationIncomplete = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return (
        status &&
        (status === EvaluationStatusEnum.INREVIEW ||
            status === EvaluationStatusEnum.INPROGRESS ||
            status === EvaluationStatusEnum.PENDING)
    );
};

export const selectIsEvaluationPendingOrInProgress = (state: RootState) => {
    const status = selectScorable(state)?.status;
    return (
        status &&
        (status === EvaluationStatusEnum.INPROGRESS ||
            status === EvaluationStatusEnum.PENDING)
    );
};

export const selectIsEvaluationDifferent = (state: RootState) =>
    !!state.scorable.data.isEvaluationDifferent;

export const selectIsSubmitAndAssign = (state: RootState) =>
    !!state.scorable.data.isSubmitAndAssign;

export const selectSideDrawerMode = (state: RootState) =>
    state.scorable.data.sideDrawerMode;

export const selectIsOperationInProgress = (state: RootState) =>
    state.scorable.data.isOperationInProgress;

export const selectIsCalibration = (state: RootState) => {
    const calibration = selectScorable(state)?.calibration;
    return calibration && calibration.selfUri;
};

export const selectHasScorableLoaded = (state: RootState) =>
    state.scorable.data.hasScorableLoaded || !!selectForm(state);

export const selectIsSurveyExpired = (state: RootState) => {
    const form = selectForm(state);
    const status = selectScorable(state)?.status;
    const loadingHasFinished = selectHasScorableLoaded(state);
    const isDisabled = form?.disabled;
    return (
        (!form && loadingHasFinished) ||
        (status && (isDisabled || status === SurveyStatusEnum.Expired))
    );
};

export const selectMCQQuestionScoreAnswer = (
    state: RootState,
    questionId: string
) => state.scorable.data.questionScores[questionId]?.answerId;

export const selectNPSQuestionScoreAnswer = (
    state: RootState,
    questionId: string
) => state.scorable.data.questionScores[questionId]?.npsScore;

export const selectAgentHasRead = (state: RootState) =>
    state.scorable.data.scorable?.agentHasRead ?? false;
export const selectReviewComments = (state: RootState) =>
    state.scorable.data.scorable?.answers?.agentComments;
export const selectAuthorizedAction = (state: RootState, action: string) =>
    !!state.scorable.data.scorable?.authorizedActions?.includes(action);

export const selectPrivateComments = (state: RootState) =>
    state.scorable.data.scorable?.answers?.privateComments;

export const selectReleaseDate = (state: RootState) =>
    state.scorable.data.scorable?.releaseDate;

export const getMaxReleaseDate = (state: RootState) => {
    if (!state.scorable.data.scorable) {
        return;
    }

    const endDate = state.scorable.data.scorable.assignedDate;

    if (endDate) {
        return moment(endDate)
            .add(DefaultLimits.releaseDateExpiry, "days")
            .format("YYYY-MM-DD");
    }
};

export const selectIsConversationAgeLimitReached = (
    state: RootState,
    conversationAgeLimit: number
) => {
    if (!state.scorable.data.scorable) {
        return false;
    }

    const endDate = state.scorable.data.scorable.assignedDate;

    if (endDate) {
        const now = moment();
        return moment(endDate).add(conversationAgeLimit, "days").isBefore(now);
    }
    return false;
};

export const selectDoesAiAnswerHaveLowConfidence = (
    state: RootState,
    questionId: string
): boolean => {
    const failureType =
        state.scorable.data.questionScores[questionId]?.aiAnswer?.failureType;

    return (
        (failureType === AiAnswerFailureTypeEnum.LowConfidenceError ||
            failureType === AiAnswerFailureTypeEnum.ServiceError) ??
        false
    );
};

export const selectDoesAiAnswerHaveLowHistoricalAccuracy = (
    state: RootState,
    questionId: string
): boolean => {
    const failureType =
        state.scorable.data.questionScores[questionId]?.aiAnswer?.failureType;

    return (
        failureType === AiAnswerFailureTypeEnum.LowHistoricalAccuracyError ||
        false
    );
};

export const selectDidAiScoringQuestionProcessingFail = (
    state: RootState,
    questionId: string
): boolean => {
    const failureType =
        state.scorable.data.questionScores[questionId]?.aiAnswer?.failureType;

    return failureType === AiAnswerFailureTypeEnum.ParsingError || false;
};

export const selectDidAiScoringProcessingFail = (state: RootState): boolean => {
    const failureType = state.scorable.data.scorable?.aiScoring?.failureType;
    return (
        (failureType === AiScoringFailureTypeEnum.ParsingError ||
            failureType === AiScoringFailureTypeEnum.ServiceError) ??
        false
    );
};

export const selectIsAiScoringQuotaReached = (state: RootState): boolean => {
    const failureType = state.scorable.data.scorable?.aiScoring?.failureType;
    return failureType === AiScoringFailureTypeEnum.QuotaReached || false;
};

export const selectIsAiScoringDuplicateFormSameAgent = (
    state: RootState
): boolean => {
    const failureType = state.scorable.data.scorable?.aiScoring?.failureType;
    return (
        failureType === AiScoringFailureTypeEnum.DuplicateFormSameAgent || false
    );
};

export const selectIsAiScoringUnauthorizedForOrg = (
    state: RootState
): boolean => {
    const failureType = state.scorable.data.scorable?.aiScoring?.failureType;
    return failureType === AiScoringFailureTypeEnum.Unauthorized || false;
};

export const selectIsAiScoringDuplicateAutomatedFormWithCopiedScore = (
    state: RootState
): boolean => {
    const isAutomatedEvaluationCompletionFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.AUTOMATED_EVALUATION_COMPLETION
    );

    if (!isAutomatedEvaluationCompletionFTEnabled) return false;

    const failureType = state.scorable.data.scorable?.aiScoring?.failureType;
    return (
        failureType ===
            AiScoringFailureTypeEnum.DuplicateAutomatedFormWithCopiedScore ||
        false
    );
};

export const selectGroupScore = (state: RootState, questionGroupId: string) =>
    state.scorable.data.questionGroupScores[questionGroupId];

export const selectQuestionScore = (state: RootState, questionId: string) =>
    state.scorable.data.questionScores[questionId];

export type TopicDetail = {
    id?: string;
    name?: string;
    transcriptPhrase?: string;
    topicPhrase?: string;
    startTimeMilliseconds?: number;
    recordingLocation?: number;
};

export const selectTopicDetails = (state: RootState, topics: string[]) => {
    const transcriptPhrases = state.scorable.data.transcriptPhrases ?? {};
    return topics.flatMap((topicId) => {
        return transcriptPhrases[topicId];
    });
};

export const selectIncludedTopicNames = (
    state: RootState,
    topicIds?: string[]
) => {
    const transcriptPhrases = state.scorable.data.transcriptPhrases ?? {};

    return topicIds
        ?.map((topicId) => {
            if (transcriptPhrases[topicId] && transcriptPhrases[topicId][0]) {
                return transcriptPhrases[topicId][0].name;
            } else {
                // we should theoretically always be able to find topic name for included topics, but in the case that something goes wrong, we'll leave an empty space in the tooltip text and log an error
                console.log(
                    "error: failed to find name of INCLUDED topic with id ",
                    topicId
                );
                return "";
            }
        })
        .join(", ");
};

export const selectExistingTopicsForAnswer = (
    answerOptionId: string,
    state: RootState,
    isMultipleSelect?: boolean
) => {
    const conditions = selectAssistedConditionForAnswer(
        answerOptionId,
        state,
        isMultipleSelect
    );
    const phrases = state.scorable.data.transcriptPhrases ?? {};
    const conditionsForExistingTopics = conditions?.filter(
        (condition) =>
            condition.operator === AssistanceConditionOperatorEnum.EXISTS
    );
    return conditionsForExistingTopics
        ?.flatMap((condition) => condition.topicIds)
        .filter(isNotNull)
        .filter((topicId) => phrases[topicId] !== undefined);
};

export const selectExcludedTopicsForAnswer = (
    answerOptionId: string,
    state: RootState,
    isMultipleSelect?: boolean
) => {
    const conditions = selectAssistedConditionForAnswer(
        answerOptionId,
        state,
        isMultipleSelect
    );
    const conditionsForExcludedTopics = conditions?.filter(
        (condition) =>
            condition.operator === AssistanceConditionOperatorEnum.NOTEXISTS
    );
    return conditionsForExcludedTopics
        ?.flatMap((condition) => condition.topicIds)
        .filter(isNotNull);
};

export const selectQuestionGroupMarkedNa = (
    state: RootState,
    questionGroupId: string
) => {
    const isNaEnabled =
        selectQuestionGroup(state, questionGroupId)?.naEnabled ?? false;
    const isMarkedNa =
        selectGroupScore(state, questionGroupId)?.markedNA ?? false;
    return isNaEnabled && isMarkedNa;
};

export const selectNpsTextAnswer =
    (questionId: string) => (state: RootState) => {
        return selectQuestionScore(state, questionId)?.npsTextAnswer;
    };

export const selectHasCalibrationPermissions = (state: RootState) => {
    return selectPermission(state, "quality:calibration:add");
};

export const selectHaveDisputePermission = (state: RootState) => {
    const formType = selectFormType(state);
    const scorable = selectScorable(state);

    if (
        formType === FormType.EVALUATION &&
        scorable &&
        scorable.authorizedActions
    ) {
        const hasDisputePermission = scorable.authorizedActions.includes(
            AuthorizedActions.DISPUTE
        );
        return hasDisputePermission;
    }
    return false;
};

export const selectHaveDisputeDeclinePermission = (state: RootState) => {
    const formType = selectFormType(state);
    const scorable = selectScorable(state);

    if (
        formType === FormType.EVALUATION &&
        scorable &&
        scorable.authorizedActions
    ) {
        return scorable.authorizedActions.includes(AuthorizedActions.DECLINE);
    }
    return false;
};

export const selectHasEditScoreOrEditPermissions = (state: RootState) => {
    const formType = selectFormType(state);
    const scorable = selectScorable(state);

    // When QM-14867 allows the PUT Evaluation API to return authorized actions,
    // replace return with selectAuthorizedAction and remove scorable && scorable.authorizedActions check
    if (
        formType === FormType.EVALUATION &&
        scorable &&
        scorable.authorizedActions
    ) {
        return (
            scorable.authorizedActions.includes(AuthorizedActions.EDIT) ||
            scorable.authorizedActions.includes(AuthorizedActions.EDIT_SCORE)
        );
    }
    return true;
};

export const selectIsEvaluationNotAssigned = (state: RootState) => {
    const scorable = selectScorable(state);
    const assignee = scorable?.assignee;
    return (
        assignee === undefined ||
        assignee?.id?.length === 0 ||
        assignee?.id === UNASSIGNED
    );
};

export const selectHasAssignPermission = (state: RootState) => {
    const formType = selectFormType(state);
    const scorable = selectScorable(state);

    // When QM-14867 allows the PUT Evaluation API to return authorized actions,
    // replace return with selectAuthorizedAction and remove scorable && scorable.authorizedActions check
    if (
        formType === FormType.EVALUATION &&
        scorable &&
        scorable.authorizedActions
    ) {
        return scorable.authorizedActions.includes(AuthorizedActions.ASSIGN);
    }
    return true;
};

export const selectHasReleasePermissions = (state: RootState) => {
    const isEvaluationAssigneeFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
    );
    if (!isEvaluationAssigneeFeatureEnabled) {
        return selectHasEditScoreOrEditPermissions(state);
    }

    const scorable = selectScorable(state);
    if (scorable && scorable.authorizedActions) {
        return scorable.authorizedActions.includes(AuthorizedActions.RELEASE);
    }
    return false;
};

export const selectAssigneeName = (state: RootState) => {
    const scorable = selectScorable(state);
    const formType = selectFormType(state);
    return formType === FormType.EVALUATION && scorable
        ? scorable.assignee?.name ?? undefined
        : undefined;
};

export const selectIsLoggedInUserAgent = (state: RootState) =>
    selectScorable(state)?.agent?.id === selectUser(state)?.id;

export const selectHasInsufficientPermissionErrorAsAssigner = (
    state: RootState
): boolean => {
    return (
        state.scorable.data.updateEvaluationErrorCode ===
        ErrorCodes.USER_CANNOT_CHANGE_ASSIGNEE_ERROR
    );
};

export const selectHasAssigneePermissionError = (state: RootState): boolean => {
    return (
        state.scorable.data.updateEvaluationErrorCode ===
        ErrorCodes.ASSIGNEE_MISSING_PERMISSIONS_FOR_ASSIGNMENT_ERROR
    );
};

export const selectHasUpdatePermissionsError = (state: RootState): boolean => {
    return (
        state.scorable.data.updateEvaluationErrorCode ===
        ErrorCodes.USER_CANNOT_UPDATE_EVALUATION_ERROR
    );
};

export const selectActiveScorableExists = (state: RootState): boolean => {
    const scorable = selectScorable(state);
    return (
        scorable !== undefined &&
        !selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        ) &&
        scorable?.status !== EvaluationStatusEnum.RETRACTED
    );
};

export const selectEvaluationCommentsHaveContent = (
    state: RootState
): boolean => {
    return (selectScorable(state)?.answers?.comments ?? "").trim().length > 0;
};
export const selectReviewCommentsHaveContent = (state: RootState): boolean => {
    return (selectReviewComments(state) ?? "").trim().length > 0;
};
export const selectPrivateCommentsHaveContent = (state: RootState): boolean => {
    return (selectPrivateComments(state) ?? "").trim().length > 0;
};

// TODO: Clean up the date check after all commentCharacterLimit related FTs are cleaned up
// This effective date check is no longer relevant since it is extremely unlikely that an evaluation will be created before the effective date at this stage.
// From now on, all evaluations most probably will be subject to comments character limit.
export const selectCommentCharLimitDateCheck = (state: RootState): boolean => {
    const assignedDate = selectScorable(state)?.assignedDate;
    if (assignedDate) {
        return moment(assignedDate).isAfter(
            moment(COMMENTS_CHARACTER_LIMIT_EFFECTIVE_DATE)
        );
    }
    return false;
};

export const selectIsPartialResponse = (state: RootState): boolean => {
    return selectScorable(state)?.missingRequiredAnswer ?? false;
};

interface Notice {
    condition?: boolean;
    messageKey: string;
}

interface SelectSubmitNoticesParameters {
    isAdmin: boolean;
    attemptDispute: boolean;
    attemptDisputeDecline: boolean;
}

export const selectSubmitNotices = (
    state: RootState,
    {
        isAdmin,
        attemptDispute,
        attemptDisputeDecline,
    }: SelectSubmitNoticesParameters
): string[] => {
    const haveReachedDisputeLimit = selectIsDisputeLimitReached(state);

    const hasDisputingExpired = selectIsConversationAgeLimitReached(
        state,
        DefaultLimits.disputeExpiry
    );

    const isDisputeEnabled = selectIsDisputeEnabled(state);
    const hasDisputePermission = selectHaveDisputePermission(state);
    const hasDisputeDeclinePermission =
        selectHaveDisputeDeclinePermission(state);
    const hasAgentReviewed = selectAgentHasRead(state) ?? false;
    const isLoggedInUserAgent = selectIsLoggedInUserAgent(state);

    const submitNotices: Notice[] = [
        {
            condition:
                isDisputeEnabled &&
                hasDisputePermission &&
                (isAdmin
                    ? attemptDispute && !isLoggedInUserAgent
                    : !hasAgentReviewed) &&
                haveReachedDisputeLimit,
            messageKey: "maxDisputes",
        },
        {
            condition:
                isDisputeEnabled &&
                hasDisputePermission &&
                (isAdmin
                    ? attemptDispute && !isLoggedInUserAgent
                    : !hasAgentReviewed) &&
                hasDisputingExpired,
            messageKey: "maxTimeDispute",
        },
        {
            condition:
                isDisputeEnabled &&
                hasDisputeDeclinePermission &&
                attemptDisputeDecline &&
                hasDisputingExpired,
            messageKey: "maxTimeDecline",
        },
    ];

    return submitNotices
        .filter((notice) => notice.condition)
        .map((notice) => notice.messageKey);
};

export const selectHasAssessmentViewPermission = (state: RootState) => {
    return selectPermission(state, QualityFormPermission.LEARNING_MODULE_VIEW);
};

export const selectIsNotAssigneeApplicable = (state: RootState) => {
    return selectScorable(state)?.assigneeApplicable === false;
};

export const selectHasEvaluationAssistanceFailed = (state: RootState) => {
    return selectScorable(state)?.hasAssistanceFailed ?? false;
};

export const selectHasAiScoringFailed = (state: RootState) => {
    const scorable = selectScorable(state);
    return (
        scorable?.aiScoring?.failureType !== undefined &&
        scorable?.aiScoring?.failureType !== null
    );
};

export const selectDoesDateLastChangedExist = (state: RootState) => {
    const scorable = selectScorable(state);
    return (
        scorable?.aiScoring?.dateLastChanged !== undefined &&
        scorable?.aiScoring?.dateLastChanged !== null
    );
};

export const selectIsAiScoringPending = (state: RootState) => {
    const scorable = selectScorable(state);
    return scorable?.aiScoring?.pending ?? false;
};

export const selectTotalVisibleQuestionsWithAiAnswer = (state: RootState) => {
    const questionIds = selectTotalVisibleQuestionsWithAiScoring(state);
    const isLensifyFTEnabled = selectFeatureToggle(
        state,
        FeatureToggle.LENSIFY_AISCORING
    );
    return questionIds.filter((questionId) => {
        const aiAnswer =
            state.scorable.data.questionScores[questionId]?.aiAnswer;
        const aiAnswerId = aiAnswer?.answerId;
        const aiAnswerFailureType = aiAnswer?.failureType;
        const isAiAnswerMarkedNAVisible =
            isLensifyFTEnabled && aiAnswer?.markedNotApplicable === true;

        return (
            aiAnswerFailureType === undefined &&
            ((aiAnswerId !== undefined && aiAnswerId !== "") ||
                isAiAnswerMarkedNAVisible)
        );
    }).length;
};

export const selectMultipleSelectQuestionOptionScore = (
    state: RootState,
    questionId: string,
    optionId: string
) => {
    const questionScore = selectQuestionScore(state, questionId);
    if (questionScore) {
        return questionScore.multipleSelectQuestionOptionScores?.find(
            (option) => {
                return option.questionId === optionId;
            }
        );
    }
};

export const selectOptionLevel = (
    state: RootState,
    questionId: string,
    optionId: string
): ScoreTagLevel => {
    const isCritical =
        selectScorableQuestion(state, optionId, true)?.isCritical ?? false;
    const isFatal =
        selectMultipleSelectQuestionOptionScore(state, questionId, optionId)
            ?.failedKillQuestion ?? false;

    return isFatal ? "fatal" : isCritical ? "critical" : "default";
};

export default slice.reducer;
